/**
 * Spot WebSocket API
 *
 * Access market data, manage accounts, and trade on Binance Spot.
 *
 * The version of the OpenAPI document: 1.0.0
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

import { WebsocketAPIBase, WebsocketApiResponse, WebsocketSendMsgOptions } from '@binance/common';
import type {
    OpenOrdersCancelAllResponse,
    OrderAmendKeepPriorityResponse,
    OrderCancelReplaceResponse,
    OrderCancelResponse,
    OrderListCancelResponse,
    OrderListPlaceOcoResponse,
    OrderListPlaceOpoResponse,
    OrderListPlaceOpocoResponse,
    OrderListPlaceOtoResponse,
    OrderListPlaceOtocoResponse,
    OrderListPlaceResponse,
    OrderPlaceResponse,
    OrderTestResponse,
    SorOrderPlaceResponse,
    SorOrderTestResponse,
} from '../types';

/**
 * TradeApi - interface
 *
 * @interface TradeApi
 */
export interface TradeApiInterface {
    /**
     * Cancel all open orders on a symbol.
     * This includes orders that are part of an order list.
     *
     * Weight(IP): 1
     *
     * Security Type: TRADE
     *
     * Notes:
     **Data Source:** Matching Engine
     *
     * @summary Cancel open orders (TRADE)
     * @param {OpenOrdersCancelAllRequest} requestParameters Request parameters.
     *
     * @returns {Promise<OpenOrdersCancelAllResponse>}
     * @memberof TradeApiInterface
     */
    openOrdersCancelAll(
        requestParameters: OpenOrdersCancelAllRequest
    ): Promise<WebsocketApiResponse<OpenOrdersCancelAllResponse>>;

    /**
     * Reduce the quantity of an existing open order.
     *
     * This adds 0 orders to the `EXCHANGE_MAX_ORDERS` filter and the `MAX_NUM_ORDERS` filter.
     *
     * Read [Order Amend Keep Priority FAQ](/products/spot/faqs/order_amend_keep_priority) to learn more.
     *
     * Weight(IP): 4
     *
     * Unfilled Order Count: 0
     *
     * Security Type: TRADE
     *
     * Notes:
     **Data Source:** Matching Engine
     *
     * @summary Order Amend Keep Priority (TRADE)
     * @param {OrderAmendKeepPriorityRequest} requestParameters Request parameters.
     *
     * @returns {Promise<OrderAmendKeepPriorityResponse>}
     * @memberof TradeApiInterface
     */
    orderAmendKeepPriority(
        requestParameters: OrderAmendKeepPriorityRequest
    ): Promise<WebsocketApiResponse<OrderAmendKeepPriorityResponse>>;

    /**
     * Cancel an active order.
     *
     * Weight(IP): 1
     *
     * Security Type: TRADE
     *
     * Notes:
     **Data Source:** Matching Engine
     *
     * Notes:
     *
     * If both `orderId` and `origClientOrderId` parameters are provided, the `orderId` is searched first, then the `origClientOrderId` from that result is checked against that order. If both conditions are not met the request will be rejected.
     *
     * `newClientOrderId` will replace `clientOrderId` of the canceled order, freeing it up for new orders.
     *
     * If you cancel an order that is a part of an order list, the entire order list is canceled.
     *
     * The performance for canceling an order (single cancel or as part of a cancel-replace) is always better when only `orderId` is sent. Sending `origClientOrderId` or both `orderId` + `origClientOrderId` will be slower.
     *
     * @summary Cancel order (TRADE)
     * @param {OrderCancelRequest} requestParameters Request parameters.
     *
     * @returns {Promise<OrderCancelResponse>}
     * @memberof TradeApiInterface
     */
    orderCancel(
        requestParameters: OrderCancelRequest
    ): Promise<WebsocketApiResponse<OrderCancelResponse>>;

    /**
     * * Cancel an existing order and immediately place a new order instead of the canceled one.
     * A new order that was not attempted (i.e. when `newOrderResult: NOT_ATTEMPTED`), will still increase the unfilled order count by 1.
     * You can only cancel an individual order from an orderList using this method, but the result is the same as canceling the entire orderList.not attempted (i.e. when `newOrderResult: NOT_ATTEMPTED`), will still increase the unfilled order count by 1.
     *
     * Weight(IP): 1
     *
     * Unfilled Order Count: 1
     *
     * Security Type: TRADE
     *
     * Notes:
     **Data Source:** Matching Engine
     *
     * Similar to the [`order.place`](#order-place) request,
     * additional mandatory parameters (*) are determined by the new order `type`.
     *
     * Available `cancelReplaceMode` options:
     *
     * `STOP_ON_FAILURE` – if cancellation request fails, new order placement will not be attempted.
     * `ALLOW_FAILURE` – new order placement will be attempted even if the cancel request fails.
     *
     * <table>
     * <thead>
     * <tr>
     * <th colspan=3 align=left>Request</th>
     * <th colspan=3 align=left>Response</th>
     * </tr>
     * <tr>
     * <th><code>cancelReplaceMode</code></th>
     * <th><code>orderRateLimitExceededMode</code></th>
     * <th>Unfilled Order Count</th>
     * <th><code>cancelResult</code></th>
     * <th><code>newOrderResult</code></th>
     * <th><code>status</code></th>
     * </tr>
     * </thead>
     * <tbody>
     * <tr>
     * <td rowspan="11"><code>STOP_ON_FAILURE</code></td>
     * <td rowspan="6"><code>DO_NOTHING</code></td>
     * <td rowspan="3">Within Limits</td>
     * <td>✅ <code>SUCCESS</code></td>
     * <td>✅ <code>SUCCESS</code></td>
     * <td align=right><code>200</code></td>
     * </tr>
     * <tr>
     * <td>❌ <code>FAILURE</code></td>
     * <td>➖ <code>NOT_ATTEMPTED</code></td>
     * <td align=right><code>400</code></td>
     * </tr>
     * <tr>
     * <td>✅ <code>SUCCESS</code></td>
     * <td>❌ <code>FAILURE</code></td>
     * <td align=right><code>409</code></td>
     * </tr>
     * <tr>
     * <td rowspan="3">Exceeds Limits</td>
     * <td>✅ <code>SUCCESS</code></td>
     * <td>✅ <code>SUCCESS</code></td>
     * <td align=right>N/A</td>
     * </tr>
     * <tr>
     * <td>❌ <code>FAILURE</code></td>
     * <td>➖ <code>NOT_ATTEMPTED</code></td>
     * <td align=right>N/A</td>
     * </tr>
     * <tr>
     * <td>✅ <code>SUCCESS</code></td>
     * <td>❌ <code>FAILURE</code></td>
     * <td align=right>N/A</td>
     * </tr>
     * <tr>
     * <td rowspan="5"><code>CANCEL_ONLY</code></td>
     * <td rowspan="3">Within Limits</td>
     * <td>✅ <code>SUCCESS</code></td>
     * <td>✅ <code>SUCCESS</code></td>
     * <td align=right><code>200</code></td>
     * </tr>
     * <tr>
     * <td>❌ <code>FAILURE</code></td>
     * <td>➖ <code>NOT_ATTEMPTED</code></td>
     * <td align=right><code>400</code></td>
     * </tr>
     * <tr>
     * <td>✅ <code>SUCCESS</code></td>
     * <td>❌ <code>FAILURE</code></td>
     * <td align=right><code>409</code></td>
     * </tr>
     * <tr>
     * <td rowspan="2">Exceeds Limits</td>
     * <td>❌ <code>FAILURE</code></td>
     * <td>➖ <code>NOT_ATTEMPTED</code></td>
     * <td align=right><code>429</code></td>
     * </tr>
     * <tr>
     * <td>✅ <code>SUCCESS</code></td>
     * <td>❌ <code>FAILURE</code></td>
     * <td align=right><code>429</code></td>
     * </tr>
     * <tr>
     * <td rowspan="16"><code>ALLOW_FAILURE</code></td>
     * <td rowspan="8"><code>DO_NOTHING</code></td>
     * <td rowspan="4">Within Limits</td>
     * <td>✅ <code>SUCCESS</code></td>
     * <td>✅ <code>SUCCESS</code></td>
     * <td align=right><code>200</code></td>
     * </tr>
     * <tr>
     * <td>❌ <code>FAILURE</code></td>
     * <td>❌ <code>FAILURE</code></td>
     * <td align=right><code>400</code></td>
     * </tr>
     * <tr>
     * <td>❌ <code>FAILURE</code></td>
     * <td>✅ <code>SUCCESS</code></td>
     * <td align=right><code>409</code></td>
     * </tr>
     * <tr>
     * <td>✅ <code>SUCCESS</code></td>
     * <td>❌ <code>FAILURE</code></td>
     * <td align=right><code>409</code></td>
     * </tr>
     * <tr>
     * <td rowspan="4">Exceeds Limits</td>
     * <td>✅ <code>SUCCESS</code></td>
     * <td>✅ <code>SUCCESS</code></td>
     * <td align=right>N/A</td>
     * </tr>
     * <tr>
     * <td>❌ <code>FAILURE</code></td>
     * <td>❌ <code>FAILURE</code></td>
     * <td align=right>N/A</td>
     * </tr>
     * <tr>
     * <td>❌ <code>FAILURE</code></td>
     * <td>✅ <code>SUCCESS</code></td>
     * <td align=right>N/A</td>
     * </tr>
     * <tr>
     * <td>✅ <code>SUCCESS</code></td>
     * <td>❌ <code>FAILURE</code></td>
     * <td align=right>N/A</td>
     * </tr>
     * <tr>
     * <td rowspan="8"><CODE>CANCEL_ONLY</CODE></td>
     * <td rowspan="4">Within Limits</td>
     * <td>✅ <code>SUCCESS</code></td>
     * <td>✅ <code>SUCCESS</code></td>
     * <td align=right><code>200</code></td>
     * </tr>
     * <tr>
     * <td>❌ <code>FAILURE</code></td>
     * <td>❌ <code>FAILURE</code></td>
     * <td align=right><code>400</code></td>
     * </tr>
     * <tr>
     * <td>❌ <code>FAILURE</code></td>
     * <td>✅ <code>SUCCESS</code></td>
     * <td align=right><code>409</code></td>
     * </tr>
     * <tr>
     * <td>✅ <code>SUCCESS</code></td>
     * <td>❌ <code>FAILURE</code></td>
     * <td align=right><code>409</code></td>
     * </tr>
     * <tr>
     * <td rowspan="4">Exceeds Limits</td>
     * <td>✅ <code>SUCCESS</code></td>
     * <td>✅ <code>SUCCESS</code></td>
     * <td align=right><code>200</code></td>
     * </tr>
     * <tr>
     * <td>❌ <code>FAILURE</code></td>
     * <td>❌ <code>FAILURE</code></td>
     * <td align=right><code>400</code></td>
     * </tr>
     * <tr>
     * <td>❌ <code>FAILURE</code></td>
     * <td>✅ <code>SUCCESS</code></td>
     * <td align=right>N/A</td>
     * </tr>
     * <tr>
     * <td>✅ <code>SUCCESS</code></td>
     * <td>❌ <code>FAILURE</code></td>
     * <td align=right><code>409</code></td>
     * </tr>
     * </tbody>
     * </table>
     *
     * Notes:
     *
     * If both `cancelOrderId` and `cancelOrigClientOrderId` parameters are provided, the `cancelOrderId` is searched first, then the `cancelOrigClientOrderId` from that result is checked against that order. If both conditions are not met the request will be rejected.
     *
     * `cancelNewClientOrderId` will replace `clientOrderId` of the canceled order, freeing it up for new orders.
     *
     * `newClientOrderId` specifies `clientOrderId` value for the placed order.
     *
     * A new order with the same `clientOrderId` is accepted only when the previous one is filled or expired.
     *
     * The new order can reuse old `clientOrderId` of the canceled order.
     *
     * This cancel-replace operation is **not transactional**.
     *
     * If one operation succeeds but the other one fails, the successful operation is still executed.
     *
     * For example, in `STOP_ON_FAILURE` mode, if the new order placement fails, the old order is still canceled.
     *
     * Filters and order count limits are evaluated before cancellation and order placement occurs.
     *
     * If new order placement is not attempted, your order count is still incremented.
     *
     * Like [`order.cancel`](#order-cancel), if you cancel an individual order from an order list, the entire order list is canceled.
     *
     * The performance for canceling an order (single cancel or as part of a cancel-replace) is always better when only `orderId` is sent. Sending `origClientOrderId` or both `orderId` + `origClientOrderId` will be slower.
     *
     * @summary Cancel and replace order (TRADE)
     * @param {OrderCancelReplaceRequest} requestParameters Request parameters.
     *
     * @returns {Promise<OrderCancelReplaceResponse>}
     * @memberof TradeApiInterface
     */
    orderCancelReplace(
        requestParameters: OrderCancelReplaceRequest
    ): Promise<WebsocketApiResponse<OrderCancelReplaceResponse>>;

    /**
     * Cancel an active order list.
     *
     * Weight(IP): 1
     *
     * Security Type: TRADE
     *
     * Notes:
     **Data Source:** Matching Engine
     *
     * Notes:
     *
     * If both `orderListId` and `listClientOrderId` parameters are provided, the `orderListId` is searched first, then the `listClientOrderId` from that result is checked against that order. If both conditions are not met the request will be rejected.
     *
     * Canceling an individual order with [`order.cancel`](#order-cancel) will cancel the entire order list as well.
     *
     * @summary Cancel Order list (TRADE)
     * @param {OrderListCancelRequest} requestParameters Request parameters.
     *
     * @returns {Promise<OrderListCancelResponse>}
     * @memberof TradeApiInterface
     */
    orderListCancel(
        requestParameters: OrderListCancelRequest
    ): Promise<WebsocketApiResponse<OrderListCancelResponse>>;

    /**
     * Send in a new one-cancels-the-other (OCO) pair:
     * `LIMIT_MAKER` + `STOP_LOSS`/`STOP_LOSS_LIMIT` orders (called *legs*),
     * where activation of one order immediately cancels the other.
     *
     * This adds 1 order to `EXCHANGE_MAX_ORDERS` filter and the `MAX_NUM_ORDERS` filter
     *
     * Weight(IP): 1
     *
     * Unfilled Order Count: 1
     *
     * Security Type: TRADE
     *
     * Notes:
     **Data Source:** Matching Engine
     *
     * Notes:
     *
     * `listClientOrderId` parameter specifies `listClientOrderId` for the OCO pair.
     *
     * A new OCO with the same `listClientOrderId` is accepted only when the previous one is filled or completely expired.
     *
     * `listClientOrderId` is distinct from `clientOrderId` of individual orders.
     *
     * `limitClientOrderId` and `stopClientOrderId` specify `clientOrderId` values for both legs of the OCO.
     *
     * A new order with the same `clientOrderId` is accepted only when the previous one is filled or expired.
     *
     * Price restrictions on the legs:
     *
     * | `side` | Price relation |
     * | ------ | -------------- |
     * | `BUY`  | `price` < market price < `stopPrice` |
     * | `SELL` | `price` > market price > `stopPrice` |
     *
     * Both legs have the same `quantity`.
     *
     * However, you can set different iceberg quantity for individual legs.
     *
     * If `stopIcebergQty` is used, `stopLimitTimeInForce` must be `GTC`.
     *
     * `trailingDelta` applies only to the `STOP_LOSS`/`STOP_LOSS_LIMIT` leg of the OCO.
     *
     * @summary Place new OCO - Deprecated (TRADE)
     * @param {OrderListPlaceRequest} requestParameters Request parameters.
     * @deprecated
     * @returns {Promise<OrderListPlaceResponse>}
     * @memberof TradeApiInterface
     */
    orderListPlace(
        requestParameters: OrderListPlaceRequest
    ): Promise<WebsocketApiResponse<OrderListPlaceResponse>>;

    /**
     * Send in an one-cancels-the-other (OCO) pair, where activation of one order immediately cancels the other.
     *
     * An OCO has 2 orders called the **above order** and **below order**.
     *
     * One of the orders must be a `LIMIT_MAKER/TAKE_PROFIT/TAKE_PROFIT_LIMIT` order and the other must be
     * `STOP_LOSS` or `STOP_LOSS_LIMIT` order.
     *
     * Price restrictions:
     * If the OCO is on the `SELL` side:
     * `LIMIT_MAKER/TAKE_PROFIT_LIMIT` `price` > Last Traded Price > `STOP_LOSS/STOP_LOSS_LIMIT`
     * `stopPrice`
     * `TAKE_PROFIT stopPrice` > Last Traded Price > `STOP_LOSS/STOP_LOSS_LIMIT
     * stopPrice`
     * If the OCO is on the `BUY` side:
     * `LIMIT_MAKER` `price` < Last Traded Price < `STOP_LOSS/STOP_LOSS_LIMIT` `stopPrice`
     * `TAKE_PROFIT stopPrice` > Last Traded Price > `STOP_LOSS/STOP_LOSS_LIMIT stopPrice`
     *  OCOs add **2 orders** to the `EXCHANGE_MAX_ORDERS` filter and `MAX_NUM_ORDERS` filter.
     *
     * Weight(IP): 1
     *
     * Unfilled Order Count: 2
     *
     * Security Type: TRADE
     *
     * Notes:
     **Data Source:** Matching Engine
     *
     * @summary Place new Order list - OCO (TRADE)
     * @param {OrderListPlaceOcoRequest} requestParameters Request parameters.
     *
     * @returns {Promise<OrderListPlaceOcoResponse>}
     * @memberof TradeApiInterface
     */
    orderListPlaceOco(
        requestParameters: OrderListPlaceOcoRequest
    ): Promise<WebsocketApiResponse<OrderListPlaceOcoResponse>>;

    /**
     * Place an [OPO](/products/spot/faqs/opo).
     *
     * OPOs add 2 orders to the EXCHANGE_MAX_NUM_ORDERS filter and MAX_NUM_ORDERS filter.
     *
     * Weight(IP): 1
     *
     * Unfilled Order Count: 2
     *
     * Security Type: TRADE
     *
     * Notes:
     **Data Source:** Matching Engine
     *
     * @summary OPO (TRADE)
     * @param {OrderListPlaceOpoRequest} requestParameters Request parameters.
     *
     * @returns {Promise<OrderListPlaceOpoResponse>}
     * @memberof TradeApiInterface
     */
    orderListPlaceOpo(
        requestParameters: OrderListPlaceOpoRequest
    ): Promise<WebsocketApiResponse<OrderListPlaceOpoResponse>>;

    /**
     * Place an [OPOCO](/products/spot/faqs/opo).
     *
     * Weight(IP): 1
     *
     * Unfilled Order Count: 3
     *
     * Security Type: TRADE
     *
     * Notes:
     **Data Source:** Matching Engine
     *
     * @summary OPOCO (TRADE)
     * @param {OrderListPlaceOpocoRequest} requestParameters Request parameters.
     *
     * @returns {Promise<OrderListPlaceOpocoResponse>}
     * @memberof TradeApiInterface
     */
    orderListPlaceOpoco(
        requestParameters: OrderListPlaceOpocoRequest
    ): Promise<WebsocketApiResponse<OrderListPlaceOpocoResponse>>;

    /**
     * Places an OTO.
     *
     * An OTO (One-Triggers-the-Other) is an order list comprised of 2 orders.
     *
     * The first order is called the **working order** and must be `LIMIT` or `LIMIT_MAKER`. Initially, only the
     * working order goes on the order book.
     *
     * The second order is called the **pending order**. It can be any order type except for `MARKET` orders using
     * parameter `quoteOrderQty`. The pending order is only placed on the order book when the working order gets
     **fully filled**.
     *
     * If either the working order or the pending order is cancelled individually, the other order in the order list
     * will also be canceled or expired.
     *
     * When the order list is placed, if the working order gets **immediately fully filled**, the placement response
     * will show the working order as `FILLED` but the pending order will still appear as `PENDING_NEW`. You need to
     * query the status of the pending order again to see its updated status.
     *
     * OTOs add **2 orders** to the `EXCHANGE_MAX_NUM_ORDERS` filter and `MAX_NUM_ORDERS` filter.
     *
     * Weight(IP): 1
     *
     * Unfilled Order Count: 2
     *
     * Security Type: TRADE
     *
     * Notes:
     **Data Source:** Matching Engine
     *
     **Mandatory parameters based on `pendingType` or `workingType`**
     *
     * Depending on the `pendingType` or `workingType`, some optional parameters will become mandatory.
     *
     * |Type                                                  |Additional mandatory parameters|Additional information|
     * |----                                                  |----                           |------
     * |`workingType` = `LIMIT`                               |`workingTimeInForce`           |
     * |`pendingType` = `LIMIT`                                |`pendingPrice`, `pendingTimeInForce`          |
     * |`pendingType` = `STOP_LOSS` or `TAKE_PROFIT`           |`pendingStopPrice` and/or `pendingTrailingDelta`|
     * |`pendingType` =`STOP_LOSS_LIMIT` or `TAKE_PROFIT_LIMIT`|`pendingPrice`, `pendingStopPrice` and/or `pendingTrailingDelta`, `pendingTimeInForce`|
     *
     * @summary Place new Order list - OTO (TRADE)
     * @param {OrderListPlaceOtoRequest} requestParameters Request parameters.
     *
     * @returns {Promise<OrderListPlaceOtoResponse>}
     * @memberof TradeApiInterface
     */
    orderListPlaceOto(
        requestParameters: OrderListPlaceOtoRequest
    ): Promise<WebsocketApiResponse<OrderListPlaceOtoResponse>>;

    /**
     * Place an OTOCO.
     *
     * An OTOCO (One-Triggers-One-Cancels-the-Other) is an order list comprised of 3 orders.
     * The first order is called the **working order** and must be `LIMIT` or `LIMIT_MAKER`. Initially, only the working order goes on the order book.
     * The behavior of the working order is the same as the [OTO](#order-list-place-oto).
     * OTOCO has 2 pending orders (pending above and pending below), forming an OCO pair. The pending orders are only placed on the order book when the working order gets **fully filled**.
     * The rules of the pending above and pending below follow the same rules as the [Order list OCO](#order-list-place-oco).
     * OTOCOs add **3 orders** to the `EXCHANGE_MAX_NUM_ORDERS` filter and `MAX_NUM_ORDERS` filter.
     *
     * Weight(IP): 1
     *
     * Unfilled Order Count: 3
     *
     * Security Type: TRADE
     *
     * Notes:
     **Data Source:** Matching Engine
     *
     **Mandatory parameters based on `pendingAboveType`, `pendingBelowType` or `workingType`**
     *
     * Depending on the `pendingAboveType`/`pendingBelowType` or `workingType`, some optional parameters will become mandatory.
     *
     * |Type                                                       |Additional mandatory parameters|Additional information|
     * |----                                                       |----                           |------
     * |`workingType` = `LIMIT`                                    |`workingTimeInForce`           |
     * |`pendingAboveType`= `LIMIT_MAKER`                                |`pendingAbovePrice`          |
     * |`pendingAboveType` = `STOP_LOSS/TAKE_PROFIT`         |`pendingAboveStopPrice` and/or `pendingAboveTrailingDelta`|
     * |`pendingAboveType=STOP_LOSS_LIMIT/TAKE_PROFIT_LIMIT`|`pendingAbovePrice`, `pendingAboveStopPrice` and/or `pendingAboveTrailingDelta`, `pendingAboveTimeInForce`|
     * |`pendingBelowType`= `LIMIT_MAKER`                                |`pendingBelowPrice`          |
     * `pendingBelowType= STOP_LOSS/TAKE_PROFIT`         |`pendingBelowStopPrice` and/or `pendingBelowTrailingDelta`|
     * |`pendingBelowType=STOP_LOSS_LIMIT/TAKE_PROFIT_LIMIT`|`pendingBelowPrice`, `pendingBelowStopPrice` and/or `pendingBelowTrailingDelta`, `pendingBelowTimeInForce`|
     *
     * @summary Place new Order list - OTOCO (TRADE)
     * @param {OrderListPlaceOtocoRequest} requestParameters Request parameters.
     *
     * @returns {Promise<OrderListPlaceOtocoResponse>}
     * @memberof TradeApiInterface
     */
    orderListPlaceOtoco(
        requestParameters: OrderListPlaceOtocoRequest
    ): Promise<WebsocketApiResponse<OrderListPlaceOtocoResponse>>;

    /**
     * Send in a new order.
     *
     * This adds 1 order to the `EXCHANGE_MAX_ORDERS` filter and the `MAX_NUM_ORDERS` filter.
     *
     * Weight(IP): 1
     *
     * Unfilled Order Count: 1
     *
     * Security Type: TRADE
     *
     * Notes:
     **Data Source:** Matching Engine
     *
     * <a id="order-type">Certain parameters (*)</a> become mandatory based on the order `type`:
     *
     * <table>
     * <thead>
     * <tr>
     * <th>Order <code>type</code></th>
     * <th>Mandatory parameters</th>
     * </tr>
     * </thead>
     * <tbody>
     * <tr>
     * <td><code>LIMIT</code></td>
     * <td>
     * <ul>
     * <li><code>timeInForce</code></li>
     * <li><code>price</code></li>
     * <li><code>quantity</code></li>
     * </ul>
     * </td>
     * </tr>
     * <tr>
     * <td><code>LIMIT_MAKER</code></td>
     * <td>
     * <ul>
     * <li><code>price</code></li>
     * <li><code>quantity</code></li>
     * </ul>
     * </td>
     * </tr>
     * <tr>
     * <td><code>MARKET</code></td>
     * <td>
     * <ul>
     * <li><code>quantity</code> or <code>quoteOrderQty</code></li>
     * </ul>
     * </td>
     * </tr>
     * <tr>
     * <td><code>STOP_LOSS</code></td>
     * <td>
     * <ul>
     * <li><code>quantity</code></li>
     * <li><code>stopPrice</code> or <code>trailingDelta</code></li>
     * </ul>
     * </td>
     * </tr>
     * <tr>
     * <td><code>STOP_LOSS_LIMIT</code></td>
     * <td>
     * <ul>
     * <li><code>timeInForce</code></li>
     * <li><code>price</code></li>
     * <li><code>quantity</code></li>
     * <li><code>stopPrice</code> or <code>trailingDelta</code></li>
     * </ul>
     * </td>
     * </tr>
     * <tr>
     * <td><code>TAKE_PROFIT</code></td>
     * <td>
     * <ul>
     * <li><code>quantity</code></li>
     * <li><code>stopPrice</code> or <code>trailingDelta</code></li>
     * </ul>
     * </td>
     * </tr>
     * <tr>
     * <td><code>TAKE_PROFIT_LIMIT</code></td>
     * <td>
     * <ul>
     * <li><code>timeInForce</code></li>
     * <li><code>price</code></li>
     * <li><code>quantity</code></li>
     * <li><code>stopPrice</code> or <code>trailingDelta</code></li>
     * </ul>
     * </td>
     * </tr>
     * </tbody>
     * </table>
     *
     * Supported order types:
     *
     * <table>
     * <thead>
     * <tr>
     * <th>Order <code>type</code></th>
     * <th>Description</th>
     * </tr>
     * </thead>
     * <tbody>
     * <tr>
     * <td><code>LIMIT</code></td>
     * <td>
     * <p>
     * Buy or sell <code>quantity</code> at the specified <code>price</code> or better.
     * </p>
     * </td>
     * </tr>
     * <tr>
     * <td><code>LIMIT_MAKER</code></td>
     * <td>
     * <p>
     * <code>LIMIT</code> order that will be rejected if it immediately matches and trades as a taker.
     * </p>
     * <p>
     * This order type is also known as a POST-ONLY order.
     * </p>
     * </td>
     * </tr>
     * <tr>
     * <td><code>MARKET</code></td>
     * <td>
     * <p>
     * Buy or sell at the best available market price.
     * </p>
     * <ul>
     * <li>
     * <p>
     * <code>MARKET</code> order with <code>quantity</code> parameter
     * specifies the amount of the <em>base asset</em> you want to buy or sell.
     * Actually executed quantity of the quote asset will be determined by available market liquidity.
     * </p>
     * <p>
     * E.g., a MARKET BUY order on BTCUSDT for <code>"quantity": "0.1000"</code>
     * specifies that you want to buy 0.1 BTC at the best available price.
     * If there is not enough BTC at the best price, keep buying at the next best price,
     * until either your order is filled, or you run out of USDT, or market runs out of BTC.
     * </p>
     * </li>
     * <li>
     * <p>
     * <code>MARKET</code> order with <code>quoteOrderQty</code> parameter
     * specifies the amount of the <em>quote asset</em> you want to spend (when buying) or receive (when selling).
     * Actually executed quantity of the base asset will be determined by available market liquidity.
     * </p>
     * <p>
     * E.g., a MARKET BUY on BTCUSDT for <code>"quoteOrderQty": "100.00"</code>
     * specifies that you want to buy as much BTC as you can for 100 USDT at the best available price.
     * Similarly, a SELL order will sell as much available BTC as needed for you to receive 100 USDT
     * (before commission).
     * </p>
     * </li>
     * </ul>
     * </td>
     * </tr>
     * <tr>
     * <td><code>STOP_LOSS</code></td>
     * <td>
     * <p>
     * Execute a <code>MARKET</code> order for given <code>quantity</code> when specified conditions are met.
     * </p>
     * <p>
     * I.e., when <code>stopPrice</code> is reached, or when <code>trailingDelta</code> is activated.
     * </p>
     * </td>
     * </tr>
     * <tr>
     * <td><code>STOP_LOSS_LIMIT</code></td>
     * <td>
     * <p>
     * Place a <code>LIMIT</code> order with given parameters when specified conditions are met.
     * </p>
     * </td>
     * </tr>
     * <tr>
     * <td><code>TAKE_PROFIT</code></td>
     * <td>
     * <p>
     * Like <code>STOP_LOSS</code> but activates when market price moves in the favorable direction.
     * </p>
     * </td>
     * </tr>
     * <tr>
     * <td><code>TAKE_PROFIT_LIMIT</code></td>
     * <td>
     * <p>
     * Like <code>STOP_LOSS_LIMIT</code> but activates when market price moves in the favorable direction.
     * </p>
     * </td>
     * </tr>
     * </tbody>
     * </table>
     *
     * <a id="pegged-orders-info"></a>
     * Notes on using parameters for Pegged Orders:
     *
     * These parameters are allowed for `LIMIT`, `LIMIT_MAKER`, `STOP_LOSS_LIMIT`, `TAKE_PROFIT_LIMIT` orders.
     * If `pegPriceType` is specified, `price` becomes optional. Otherwise, it is still mandatory.
     * `pegPriceType=PRIMARY_PEG` means the primary peg, that is the best price on the same side of the order book as your order.
     * `pegPriceType=MARKET_PEG` means the market peg, that is the best price on the opposite side of the order book from your order.
     * Use `pegOffsetType` and `pegOffsetValue` to request a price level other than the best one. These parameters must be specified together.
     *
     * <a id="timeInForce"></a>
     *
     * Available `timeInForce` options,
     * setting how long the order should be active before expiration:
     *
     * TIF  | Description
     * ----- | --------------
     * `GTC` | **Good 'til Canceled** – the order will remain on the book until you cancel it, or the order is completely filled.
     * `IOC` | **Immediate or Cancel** – the order will be filled for as much as possible, the unfilled quantity immediately expires.
     * `FOK` | **Fill or Kill** – the order will expire unless it cannot be immediately filled for the entire quantity.
     *
     * Notes:
     *
     * `newClientOrderId` specifies `clientOrderId` value for the order.
     *
     * A new order with the same `clientOrderId` is accepted only when the previous one is filled or expired.
     *
     * Any `LIMIT` or `LIMIT_MAKER` order can be made into an iceberg order by specifying the `icebergQty`.
     *
     * An order with an `icebergQty` must have `timeInForce` set to `GTC`.
     *
     * Trigger order price rules for `STOP_LOSS`/`TAKE_PROFIT` orders:
     *
     * `stopPrice` must be above market price: `STOP_LOSS BUY`, `TAKE_PROFIT SELL`
     * `stopPrice` must be below market price: `STOP_LOSS SELL`, `TAKE_PROFIT BUY`
     *
     * `MARKET` orders using `quoteOrderQty` follow [`LOT_SIZE`](/products/spot/filters#lot_size) filter rules.
     *
     * The order will execute a quantity that has notional value as close as possible to requested `quoteOrderQty`.
     *
     * @summary Place new order (TRADE)
     * @param {OrderPlaceRequest} requestParameters Request parameters.
     *
     * @returns {Promise<OrderPlaceResponse>}
     * @memberof TradeApiInterface
     */
    orderPlace(
        requestParameters: OrderPlaceRequest
    ): Promise<WebsocketApiResponse<OrderPlaceResponse>>;

    /**
     * Test order placement.
     *
     * Validates new order parameters and verifies your signature
     * but does not send the order into the matching engine.
     *
     * Weight: | Condition | Request Weight |
     * | --- | --- |
     * | Without `computeCommissionRates` | 1 |
     * | With `computeCommissionRates` | 20 |
     *
     * Security Type: TRADE
     *
     * Notes:
     **Data Source:** Memory
     *
     * @summary Test new order (TRADE)
     * @param {OrderTestRequest} requestParameters Request parameters.
     *
     * @returns {Promise<OrderTestResponse>}
     * @memberof TradeApiInterface
     */
    orderTest(
        requestParameters: OrderTestRequest
    ): Promise<WebsocketApiResponse<OrderTestResponse>>;

    /**
     * Places an order using smart order routing (SOR).
     *
     * This adds 1 order to the `EXCHANGE_MAX_ORDERS` filter and the `MAX_NUM_ORDERS` filter.
     *
     * Read [SOR FAQ](/products/spot/faqs/sor_faq) to learn more.
     *
     * Weight(IP): 1
     *
     * Unfilled Order Count: 1
     *
     * Security Type: TRADE
     *
     * Notes:
     **Data Source:** Matching Engine
     *
     **Note:** `sor.order.place` only supports `LIMIT` and `MARKET` orders. `quoteOrderQty` is not supported.
     *
     * @summary Place new order using SOR (TRADE)
     * @param {SorOrderPlaceRequest} requestParameters Request parameters.
     *
     * @returns {Promise<SorOrderPlaceResponse>}
     * @memberof TradeApiInterface
     */
    sorOrderPlace(
        requestParameters: SorOrderPlaceRequest
    ): Promise<WebsocketApiResponse<SorOrderPlaceResponse>>;

    /**
     * Test new order creation and signature/recvWindow using smart order routing (SOR).
     * Creates and validates a new order but does not send it into the matching engine.
     *
     * Weight: | Condition | Request Weight |
     * | --- | --- |
     * | Without `computeCommissionRates` | 1 |
     * | With `computeCommissionRates` | 20 |
     *
     * Security Type: TRADE
     *
     * Notes:
     **Data Source:** Memory
     *
     * @summary Test new order using SOR (TRADE)
     * @param {SorOrderTestRequest} requestParameters Request parameters.
     *
     * @returns {Promise<SorOrderTestResponse>}
     * @memberof TradeApiInterface
     */
    sorOrderTest(
        requestParameters: SorOrderTestRequest
    ): Promise<WebsocketApiResponse<SorOrderTestResponse>>;
}

/**
 * Request parameters for openOrdersCancelAll operation in TradeApi.
 * @interface OpenOrdersCancelAllRequest
 */
export interface OpenOrdersCancelAllRequest {
    /**
     *
     * @type {string}
     * @memberof TradeApiOpenOrdersCancelAll
     */
    readonly symbol: string;

    /**
     * Client-generated request identifier.
     * @type {string}
     * @memberof TradeApiOpenOrdersCancelAll
     */
    readonly id?: string;

    /**
     * Supports up to three decimal places of precision (e.g., 6000.346) so that microseconds may be specified.
     * @type {number}
     * @memberof TradeApiOpenOrdersCancelAll
     */
    readonly recvWindow?: number;
}

/**
 * Request parameters for orderAmendKeepPriority operation in TradeApi.
 * @interface OrderAmendKeepPriorityRequest
 */
export interface OrderAmendKeepPriorityRequest {
    /**
     *
     * @type {string}
     * @memberof TradeApiOrderAmendKeepPriority
     */
    readonly symbol: string;

    /**
     * `newQty` must be greater than 0 and less than the order's quantity.
     * @type {number}
     * @memberof TradeApiOrderAmendKeepPriority
     */
    readonly newQty: number;

    /**
     * Client-generated request identifier.
     * @type {string}
     * @memberof TradeApiOrderAmendKeepPriority
     */
    readonly id?: string;

    /**
     * `orderId` or `origClientOrderId` must be sent
     * @type {number | bigint}
     * @memberof TradeApiOrderAmendKeepPriority
     */
    readonly orderId?: number | bigint;

    /**
     * `orderId` or `origClientOrderId` must be sent
     * @type {string}
     * @memberof TradeApiOrderAmendKeepPriority
     */
    readonly origClientOrderId?: string;

    /**
     * The new client order ID for the order after being amended. <br> If not sent, one will be randomly generated. <br> It is possible to reuse the current clientOrderId by sending it as the `newClientOrderId`.
     * @type {string}
     * @memberof TradeApiOrderAmendKeepPriority
     */
    readonly newClientOrderId?: string;

    /**
     * Supports up to three decimal places of precision (e.g., 6000.346) so that microseconds may be specified.
     * @type {number}
     * @memberof TradeApiOrderAmendKeepPriority
     */
    readonly recvWindow?: number;
}

/**
 * Request parameters for orderCancel operation in TradeApi.
 * @interface OrderCancelRequest
 */
export interface OrderCancelRequest {
    /**
     *
     * @type {string}
     * @memberof TradeApiOrderCancel
     */
    readonly symbol: string;

    /**
     * Client-generated request identifier.
     * @type {string}
     * @memberof TradeApiOrderCancel
     */
    readonly id?: string;

    /**
     *
     * @type {number | bigint}
     * @memberof TradeApiOrderCancel
     */
    readonly orderId?: number | bigint;

    /**
     *
     * @type {string}
     * @memberof TradeApiOrderCancel
     */
    readonly origClientOrderId?: string;

    /**
     * Used to uniquely identify this cancel. Automatically generated by default.
     * @type {string}
     * @memberof TradeApiOrderCancel
     */
    readonly newClientOrderId?: string;

    /**
     * Supported values: <br>`ONLY_NEW` - Cancel will succeed if the order status is `NEW`.<br> `ONLY_PARTIALLY_FILLED` - Cancel will succeed if order status is `PARTIALLY_FILLED`.
     * @type {'ONLY_NEW' | 'ONLY_PARTIALLY_FILLED'}
     * @memberof TradeApiOrderCancel
     */
    readonly cancelRestrictions?: OrderCancelCancelRestrictionsEnum;

    /**
     * Supports up to three decimal places of precision (e.g., 6000.346) so that microseconds may be specified.
     * @type {number}
     * @memberof TradeApiOrderCancel
     */
    readonly recvWindow?: number;
}

/**
 * Request parameters for orderCancelReplace operation in TradeApi.
 * @interface OrderCancelReplaceRequest
 */
export interface OrderCancelReplaceRequest {
    /**
     *
     * @type {string}
     * @memberof TradeApiOrderCancelReplace
     */
    readonly symbol: string;

    /**
     * The allowed values are: <br/> `STOP_ON_FAILURE` - If the cancel request fails, the new order placement will not be attempted. <br/> `ALLOW_FAILURE` - new order placement will be attempted even if cancel request fails.
     * @type {'STOP_ON_FAILURE' | 'ALLOW_FAILURE'}
     * @memberof TradeApiOrderCancelReplace
     */
    readonly cancelReplaceMode: OrderCancelReplaceCancelReplaceModeEnum;

    /**
     * Please see [Enums](/products/spot/enums#side) for supported values.
     * @type {'BUY' | 'SELL'}
     * @memberof TradeApiOrderCancelReplace
     */
    readonly side: OrderCancelReplaceSideEnum;

    /**
     * Please see [Enums](/products/spot/enums#ordertypes) for supported values.
     * @type {'MARKET' | 'LIMIT' | 'STOP_LOSS' | 'STOP_LOSS_LIMIT' | 'TAKE_PROFIT' | 'TAKE_PROFIT_LIMIT' | 'LIMIT_MAKER'}
     * @memberof TradeApiOrderCancelReplace
     */
    readonly type: OrderCancelReplaceTypeEnum;

    /**
     * Client-generated request identifier.
     * @type {string}
     * @memberof TradeApiOrderCancelReplace
     */
    readonly id?: string;

    /**
     * Either `cancelOrderId` or `cancelOrigClientOrderId` must be sent. <br></br>If both `cancelOrderId` and `cancelOrigClientOrderId` parameters are provided, the `cancelOrderId` is searched first, then the `cancelOrigClientOrderId` from that result is checked against that order. <br></br>If both conditions are not met the request will be rejected.
     * @type {number | bigint}
     * @memberof TradeApiOrderCancelReplace
     */
    readonly cancelOrderId?: number | bigint;

    /**
     * Either `cancelOrderId` or `cancelOrigClientOrderId` must be sent. <br></br> If both `cancelOrderId` and `cancelOrigClientOrderId` parameters are provided, the `cancelOrderId` is searched first, then the `cancelOrigClientOrderId` from that result is checked against that order. <br></br> If both conditions are not met the request will be rejected.
     * @type {string}
     * @memberof TradeApiOrderCancelReplace
     */
    readonly cancelOrigClientOrderId?: string;

    /**
     * Used to uniquely identify this cancel. Automatically generated by default.
     * @type {string}
     * @memberof TradeApiOrderCancelReplace
     */
    readonly cancelNewClientOrderId?: string;

    /**
     * Please see [Enums](/products/spot/enums#timeinforce) for supported values.
     * @type {'GTC' | 'IOC' | 'FOK'}
     * @memberof TradeApiOrderCancelReplace
     */
    readonly timeInForce?: OrderCancelReplaceTimeInForceEnum;

    /**
     *
     * @type {number}
     * @memberof TradeApiOrderCancelReplace
     */
    readonly price?: number;

    /**
     *
     * @type {number}
     * @memberof TradeApiOrderCancelReplace
     */
    readonly quantity?: number;

    /**
     *
     * @type {number}
     * @memberof TradeApiOrderCancelReplace
     */
    readonly quoteOrderQty?: number;

    /**
     * Used to identify the new order.
     * @type {string}
     * @memberof TradeApiOrderCancelReplace
     */
    readonly newClientOrderId?: string;

    /**
     * Allowed values: <br/> `ACK`, `RESULT`, `FULL` <br/> `MARKET` and `LIMIT` orders types default to `FULL`; all other orders default to `ACK`
     * @type {'ACK' | 'RESULT' | 'FULL'}
     * @memberof TradeApiOrderCancelReplace
     */
    readonly newOrderRespType?: OrderCancelReplaceNewOrderRespTypeEnum;

    /**
     * Used with `STOP_LOSS`, `STOP_LOSS_LIMIT`, `TAKE_PROFIT`, and `TAKE_PROFIT_LIMIT` orders.
     * @type {number}
     * @memberof TradeApiOrderCancelReplace
     */
    readonly stopPrice?: number;

    /**
     * See [Trailing Stop order FAQ](/products/spot/faqs/trailing-stop-faq)
     * @type {number}
     * @memberof TradeApiOrderCancelReplace
     */
    readonly trailingDelta?: number;

    /**
     * Used with `LIMIT`, `STOP_LOSS_LIMIT`, and `TAKE_PROFIT_LIMIT` to create an iceberg order.
     * @type {number}
     * @memberof TradeApiOrderCancelReplace
     */
    readonly icebergQty?: number;

    /**
     *
     * @type {number | bigint}
     * @memberof TradeApiOrderCancelReplace
     */
    readonly strategyId?: number | bigint;

    /**
     * The value cannot be less than `1000000`.
     * @type {number}
     * @memberof TradeApiOrderCancelReplace
     */
    readonly strategyType?: number;

    /**
     * The allowed enums is dependent on what is configured on the symbol. The possible supported values are: [STP Modes](/products/spot/enums#stpmodes).
     * @type {'NONE' | 'EXPIRE_TAKER' | 'EXPIRE_MAKER' | 'EXPIRE_BOTH' | 'DECREMENT' | 'TRANSFER'}
     * @memberof TradeApiOrderCancelReplace
     */
    readonly selfTradePreventionMode?: OrderCancelReplaceSelfTradePreventionModeEnum;

    /**
     * Supported values: <br>`ONLY_NEW` - Cancel will succeed if the order status is `NEW`.<br> `ONLY_PARTIALLY_FILLED` - Cancel will succeed if order status is `PARTIALLY_FILLED`.
     * @type {'ONLY_NEW' | 'ONLY_PARTIALLY_FILLED'}
     * @memberof TradeApiOrderCancelReplace
     */
    readonly cancelRestrictions?: OrderCancelReplaceCancelRestrictionsEnum;

    /**
     * Supported values: <br> `DO_NOTHING` (default)- will only attempt to cancel the order if account has not exceeded the unfilled order rate limit<br> `CANCEL_ONLY` - will always cancel the order
     * @type {'DO_NOTHING' | 'CANCEL_ONLY'}
     * @memberof TradeApiOrderCancelReplace
     */
    readonly orderRateLimitExceededMode?: OrderCancelReplaceOrderRateLimitExceededModeEnum;

    /**
     * `PRIMARY_PEG` or `MARKET_PEG` <br> See Pegged Orders
     * @type {'PRIMARY_PEG' | 'MARKET_PEG'}
     * @memberof TradeApiOrderCancelReplace
     */
    readonly pegPriceType?: OrderCancelReplacePegPriceTypeEnum;

    /**
     * Price level to peg the price to (max: 100) <br> See Pegged Orders
     * @type {number}
     * @memberof TradeApiOrderCancelReplace
     */
    readonly pegOffsetValue?: number;

    /**
     * Only `PRICE_LEVEL` is supported <br> See Pegged Orders
     * @type {'PRICE_LEVEL'}
     * @memberof TradeApiOrderCancelReplace
     */
    readonly pegOffsetType?: OrderCancelReplacePegOffsetTypeEnum;

    /**
     * Supports up to three decimal places of precision (e.g., 6000.346) so that microseconds may be specified.
     * @type {number}
     * @memberof TradeApiOrderCancelReplace
     */
    readonly recvWindow?: number;
}

/**
 * Request parameters for orderListCancel operation in TradeApi.
 * @interface OrderListCancelRequest
 */
export interface OrderListCancelRequest {
    /**
     *
     * @type {string}
     * @memberof TradeApiOrderListCancel
     */
    readonly symbol: string;

    /**
     * Client-generated request identifier.
     * @type {string}
     * @memberof TradeApiOrderListCancel
     */
    readonly id?: string;

    /**
     * Either `orderListId` or `listClientOrderId` must be provided
     * @type {number}
     * @memberof TradeApiOrderListCancel
     */
    readonly orderListId?: number;

    /**
     * Either `orderListId` or `listClientOrderId` must be provided
     * @type {string}
     * @memberof TradeApiOrderListCancel
     */
    readonly listClientOrderId?: string;

    /**
     * Used to uniquely identify this cancel. Automatically generated by default.
     * @type {string}
     * @memberof TradeApiOrderListCancel
     */
    readonly newClientOrderId?: string;

    /**
     * Supports up to three decimal places of precision (e.g., 6000.346) so that microseconds may be specified.
     * @type {number}
     * @memberof TradeApiOrderListCancel
     */
    readonly recvWindow?: number;
}

/**
 * Request parameters for orderListPlace operation in TradeApi.
 * @interface OrderListPlaceRequest
 */
export interface OrderListPlaceRequest {
    /**
     *
     * @type {string}
     * @memberof TradeApiOrderListPlace
     */
    readonly symbol: string;

    /**
     * Please see [Enums](/products/spot/enums#side) for supported values.
     * @type {'BUY' | 'SELL'}
     * @memberof TradeApiOrderListPlace
     */
    readonly side: OrderListPlaceSideEnum;

    /**
     *
     * @type {number}
     * @memberof TradeApiOrderListPlace
     */
    readonly price: number;

    /**
     *
     * @type {number}
     * @memberof TradeApiOrderListPlace
     */
    readonly quantity: number;

    /**
     * Client-generated request identifier.
     * @type {string}
     * @memberof TradeApiOrderListPlace
     */
    readonly id?: string;

    /**
     * A unique Id for the entire orderList
     * @type {string}
     * @memberof TradeApiOrderListPlace
     */
    readonly listClientOrderId?: string;

    /**
     * A unique Id for the limit order
     * @type {string}
     * @memberof TradeApiOrderListPlace
     */
    readonly limitClientOrderId?: string;

    /**
     * Used to make the `LIMIT_MAKER` leg an iceberg order.
     * @type {number}
     * @memberof TradeApiOrderListPlace
     */
    readonly limitIcebergQty?: number;

    /**
     *
     * @type {number | bigint}
     * @memberof TradeApiOrderListPlace
     */
    readonly limitStrategyId?: number | bigint;

    /**
     * The value cannot be less than `1000000`.
     * @type {number}
     * @memberof TradeApiOrderListPlace
     */
    readonly limitStrategyType?: number;

    /**
     *
     * @type {number}
     * @memberof TradeApiOrderListPlace
     */
    readonly stopPrice?: number;

    /**
     *
     * @type {number}
     * @memberof TradeApiOrderListPlace
     */
    readonly trailingDelta?: number;

    /**
     * A unique Id for the stop loss/stop loss limit leg
     * @type {string}
     * @memberof TradeApiOrderListPlace
     */
    readonly stopClientOrderId?: string;

    /**
     * If provided, `stopLimitTimeInForce` is required.
     * @type {number}
     * @memberof TradeApiOrderListPlace
     */
    readonly stopLimitPrice?: number;

    /**
     * Valid values are `GTC`/`FOK`/`IOC`
     * @type {'GTC' | 'IOC' | 'FOK'}
     * @memberof TradeApiOrderListPlace
     */
    readonly stopLimitTimeInForce?: OrderListPlaceStopLimitTimeInForceEnum;

    /**
     * Used with `STOP_LOSS_LIMIT` leg to make an iceberg order.
     * @type {number}
     * @memberof TradeApiOrderListPlace
     */
    readonly stopIcebergQty?: number;

    /**
     *
     * @type {number | bigint}
     * @memberof TradeApiOrderListPlace
     */
    readonly stopStrategyId?: number | bigint;

    /**
     * The value cannot be less than `1000000`.
     * @type {number}
     * @memberof TradeApiOrderListPlace
     */
    readonly stopStrategyType?: number;

    /**
     * Format of the JSON response. Supported values: [Order Response Type](/products/spot/enums#orderresponsetype)
     * @type {'ACK' | 'RESULT' | 'FULL'}
     * @memberof TradeApiOrderListPlace
     */
    readonly newOrderRespType?: OrderListPlaceNewOrderRespTypeEnum;

    /**
     * The allowed values are dependent on what is configured on the symbol. Supported values: [STP Modes](/products/spot/enums#stpmodes)
     * @type {'NONE' | 'EXPIRE_TAKER' | 'EXPIRE_MAKER' | 'EXPIRE_BOTH' | 'DECREMENT' | 'TRANSFER'}
     * @memberof TradeApiOrderListPlace
     */
    readonly selfTradePreventionMode?: OrderListPlaceSelfTradePreventionModeEnum;

    /**
     * Supports up to three decimal places of precision (e.g., 6000.346) so that microseconds may be specified.
     * @type {number}
     * @memberof TradeApiOrderListPlace
     */
    readonly recvWindow?: number;
}

/**
 * Request parameters for orderListPlaceOco operation in TradeApi.
 * @interface OrderListPlaceOcoRequest
 */
export interface OrderListPlaceOcoRequest {
    /**
     *
     * @type {string}
     * @memberof TradeApiOrderListPlaceOco
     */
    readonly symbol: string;

    /**
     * `BUY` or `SELL`
     * @type {'BUY' | 'SELL'}
     * @memberof TradeApiOrderListPlaceOco
     */
    readonly side: OrderListPlaceOcoSideEnum;

    /**
     * Quantity for both orders of the order list.
     * @type {number}
     * @memberof TradeApiOrderListPlaceOco
     */
    readonly quantity: number;

    /**
     *
     * @type {'STOP_LOSS_LIMIT' | 'STOP_LOSS' | 'LIMIT_MAKER' | 'TAKE_PROFIT' | 'TAKE_PROFIT_LIMIT'}
     * @memberof TradeApiOrderListPlaceOco
     */
    readonly aboveType: OrderListPlaceOcoAboveTypeEnum;

    /**
     * Supported values: `STOP_LOSS`, `STOP_LOSS_LIMIT`, `TAKE_PROFIT`, `TAKE_PROFIT_LIMIT`
     * @type {'STOP_LOSS' | 'STOP_LOSS_LIMIT' | 'TAKE_PROFIT' | 'TAKE_PROFIT_LIMIT'}
     * @memberof TradeApiOrderListPlaceOco
     */
    readonly belowType: OrderListPlaceOcoBelowTypeEnum;

    /**
     * Client-generated request identifier.
     * @type {string}
     * @memberof TradeApiOrderListPlaceOco
     */
    readonly id?: string;

    /**
     * Arbitrary unique ID among open order lists. Automatically generated if not sent. A new order list with the same `listClientOrderId` is accepted only when the previous one is filled or completely expired. `listClientOrderId` is distinct from the `aboveClientOrderId` and the `belowClientOrderId`.
     * @type {string}
     * @memberof TradeApiOrderListPlaceOco
     */
    readonly listClientOrderId?: string;

    /**
     * Arbitrary unique ID among open orders for the above order. Automatically generated if not sent.
     * @type {string}
     * @memberof TradeApiOrderListPlaceOco
     */
    readonly aboveClientOrderId?: string;

    /**
     * Note that this can only be used if `aboveTimeInForce` is `GTC`.
     * @type {number | bigint}
     * @memberof TradeApiOrderListPlaceOco
     */
    readonly aboveIcebergQty?: number | bigint;

    /**
     * Can be used if `aboveType` is `STOP_LOSS_LIMIT`, `LIMIT_MAKER`, or `TAKE_PROFIT_LIMIT` to specify the limit price.
     * @type {number}
     * @memberof TradeApiOrderListPlaceOco
     */
    readonly abovePrice?: number;

    /**
     * Can be used if `aboveType` is `STOP_LOSS`, `STOP_LOSS_LIMIT`, `TAKE_PROFIT`, `TAKE_PROFIT_LIMIT`. Either `aboveStopPrice` or `aboveTrailingDelta` or both, must be specified.
     * @type {number}
     * @memberof TradeApiOrderListPlaceOco
     */
    readonly aboveStopPrice?: number;

    /**
     * See [Trailing Stop order FAQ](/products/spot/faqs/trailing-stop-faq)
     * @type {number | bigint}
     * @memberof TradeApiOrderListPlaceOco
     */
    readonly aboveTrailingDelta?: number | bigint;

    /**
     * Required if `aboveType` is `STOP_LOSS_LIMIT` or `TAKE_PROFIT_LIMIT`.
     * @type {'GTC' | 'IOC' | 'FOK'}
     * @memberof TradeApiOrderListPlaceOco
     */
    readonly aboveTimeInForce?: OrderListPlaceOcoAboveTimeInForceEnum;

    /**
     * Arbitrary numeric value identifying the above order within an order strategy.
     * @type {number | bigint}
     * @memberof TradeApiOrderListPlaceOco
     */
    readonly aboveStrategyId?: number | bigint;

    /**
     * Arbitrary numeric value identifying the above order strategy. Values smaller than `1000000` are reserved and cannot be used.
     * @type {number}
     * @memberof TradeApiOrderListPlaceOco
     */
    readonly aboveStrategyType?: number;

    /**
     * `PRIMARY_PEG` or `MARKET_PEG`. See [Pegged Orders](/products/spot/faqs/pegged_orders)
     * @type {'PRIMARY_PEG' | 'MARKET_PEG'}
     * @memberof TradeApiOrderListPlaceOco
     */
    readonly abovePegPriceType?: OrderListPlaceOcoAbovePegPriceTypeEnum;

    /**
     *
     * @type {'PRICE_LEVEL'}
     * @memberof TradeApiOrderListPlaceOco
     */
    readonly abovePegOffsetType?: OrderListPlaceOcoAbovePegOffsetTypeEnum;

    /**
     *
     * @type {number}
     * @memberof TradeApiOrderListPlaceOco
     */
    readonly abovePegOffsetValue?: number;

    /**
     * Arbitrary unique ID among open orders for the below order. Automatically generated if not sent.
     * @type {string}
     * @memberof TradeApiOrderListPlaceOco
     */
    readonly belowClientOrderId?: string;

    /**
     * Note that this can only be used if `belowTimeInForce` is `GTC`.
     * @type {number | bigint}
     * @memberof TradeApiOrderListPlaceOco
     */
    readonly belowIcebergQty?: number | bigint;

    /**
     * Can be used if `belowType` is `STOP_LOSS_LIMIT`, `LIMIT_MAKER`, or `TAKE_PROFIT_LIMIT` to specify the limit price.
     * @type {number}
     * @memberof TradeApiOrderListPlaceOco
     */
    readonly belowPrice?: number;

    /**
     * Can be used if `belowType` is `STOP_LOSS`, `STOP_LOSS_LIMIT`, `TAKE_PROFIT`, `TAKE_PROFIT_LIMIT`. Either `belowStopPrice` or `belowTrailingDelta` or both, must be specified.
     * @type {number}
     * @memberof TradeApiOrderListPlaceOco
     */
    readonly belowStopPrice?: number;

    /**
     * See [Trailing Stop order FAQ](/products/spot/faqs/trailing-stop-faq)
     * @type {number | bigint}
     * @memberof TradeApiOrderListPlaceOco
     */
    readonly belowTrailingDelta?: number | bigint;

    /**
     * Required if `belowType` is `STOP_LOSS_LIMIT` or `TAKE_PROFIT_LIMIT`.
     * @type {'GTC' | 'IOC' | 'FOK'}
     * @memberof TradeApiOrderListPlaceOco
     */
    readonly belowTimeInForce?: OrderListPlaceOcoBelowTimeInForceEnum;

    /**
     * Arbitrary numeric value identifying the below order within an order strategy.
     * @type {number | bigint}
     * @memberof TradeApiOrderListPlaceOco
     */
    readonly belowStrategyId?: number | bigint;

    /**
     * Arbitrary numeric value identifying the below order strategy. Values smaller than `1000000` are reserved and cannot be used.
     * @type {number}
     * @memberof TradeApiOrderListPlaceOco
     */
    readonly belowStrategyType?: number;

    /**
     * See [Pegged Orders](/products/spot/faqs/pegged_orders)
     * @type {'PRIMARY_PEG' | 'MARKET_PEG'}
     * @memberof TradeApiOrderListPlaceOco
     */
    readonly belowPegPriceType?: OrderListPlaceOcoBelowPegPriceTypeEnum;

    /**
     *
     * @type {'PRICE_LEVEL'}
     * @memberof TradeApiOrderListPlaceOco
     */
    readonly belowPegOffsetType?: OrderListPlaceOcoBelowPegOffsetTypeEnum;

    /**
     *
     * @type {number}
     * @memberof TradeApiOrderListPlaceOco
     */
    readonly belowPegOffsetValue?: number;

    /**
     * Select response format: `ACK`, `RESULT`, `FULL`.
     * @type {'ACK' | 'RESULT' | 'FULL'}
     * @memberof TradeApiOrderListPlaceOco
     */
    readonly newOrderRespType?: OrderListPlaceOcoNewOrderRespTypeEnum;

    /**
     * The allowed enums is dependent on what is configured on the symbol. Supported values: [STP Modes](/products/spot/enums#stpmodes)
     * @type {'NONE' | 'EXPIRE_TAKER' | 'EXPIRE_MAKER' | 'EXPIRE_BOTH' | 'DECREMENT' | 'TRANSFER'}
     * @memberof TradeApiOrderListPlaceOco
     */
    readonly selfTradePreventionMode?: OrderListPlaceOcoSelfTradePreventionModeEnum;

    /**
     * Supports up to three decimal places of precision (e.g., 6000.346) so that microseconds may be specified.
     * @type {number}
     * @memberof TradeApiOrderListPlaceOco
     */
    readonly recvWindow?: number;
}

/**
 * Request parameters for orderListPlaceOpo operation in TradeApi.
 * @interface OrderListPlaceOpoRequest
 */
export interface OrderListPlaceOpoRequest {
    /**
     *
     * @type {string}
     * @memberof TradeApiOrderListPlaceOpo
     */
    readonly symbol: string;

    /**
     * Supported values: `LIMIT`, `LIMIT_MAKER`
     * @type {'LIMIT' | 'LIMIT_MAKER'}
     * @memberof TradeApiOrderListPlaceOpo
     */
    readonly workingType: OrderListPlaceOpoWorkingTypeEnum;

    /**
     * Supported values: [Order Side](/products/spot/enums#side)
     * @type {'BUY' | 'SELL'}
     * @memberof TradeApiOrderListPlaceOpo
     */
    readonly workingSide: OrderListPlaceOpoWorkingSideEnum;

    /**
     * Price for the working order.
     * @type {number}
     * @memberof TradeApiOrderListPlaceOpo
     */
    readonly workingPrice: number;

    /**
     * Sets the quantity for the working order.
     * @type {number}
     * @memberof TradeApiOrderListPlaceOpo
     */
    readonly workingQuantity: number;

    /**
     * Supported values: [Order Types](/products/spot/enums#ordertypes). Note that `MARKET` orders using `quoteOrderQty` are not supported.
     * @type {'LIMIT' | 'MARKET' | 'STOP_LOSS' | 'STOP_LOSS_LIMIT' | 'TAKE_PROFIT' | 'TAKE_PROFIT_LIMIT' | 'LIMIT_MAKER'}
     * @memberof TradeApiOrderListPlaceOpo
     */
    readonly pendingType: OrderListPlaceOpoPendingTypeEnum;

    /**
     * Supported values: [Order Side](/products/spot/enums#side)
     * @type {'BUY' | 'SELL'}
     * @memberof TradeApiOrderListPlaceOpo
     */
    readonly pendingSide: OrderListPlaceOpoPendingSideEnum;

    /**
     * Client-generated request identifier.
     * @type {string}
     * @memberof TradeApiOrderListPlaceOpo
     */
    readonly id?: string;

    /**
     * Arbitrary unique ID among open order lists. Automatically generated if not sent. A new order list with the same `listClientOrderId` is accepted only when the previous one is filled or completely expired. `listClientOrderId` is distinct from the `workingClientOrderId` and the `pendingClientOrderId`.
     * @type {string}
     * @memberof TradeApiOrderListPlaceOpo
     */
    readonly listClientOrderId?: string;

    /**
     * Format of the JSON response. Supported values: [Order Response Type](/products/spot/enums#orderresponsetype)
     * @type {'ACK' | 'RESULT' | 'FULL'}
     * @memberof TradeApiOrderListPlaceOpo
     */
    readonly newOrderRespType?: OrderListPlaceOpoNewOrderRespTypeEnum;

    /**
     * The allowed enums is dependent on what is configured on the symbol. Supported values: [STP Modes](/products/spot/enums#stpmodes)
     * @type {'NONE' | 'EXPIRE_TAKER' | 'EXPIRE_MAKER' | 'EXPIRE_BOTH' | 'DECREMENT' | 'TRANSFER'}
     * @memberof TradeApiOrderListPlaceOpo
     */
    readonly selfTradePreventionMode?: OrderListPlaceOpoSelfTradePreventionModeEnum;

    /**
     * Arbitrary unique ID among open orders for the working order. Automatically generated if not sent.
     * @type {string}
     * @memberof TradeApiOrderListPlaceOpo
     */
    readonly workingClientOrderId?: string;

    /**
     * This can only be used if `workingTimeInForce` is `GTC`, or if `workingType` is `LIMIT_MAKER`.
     * @type {number}
     * @memberof TradeApiOrderListPlaceOpo
     */
    readonly workingIcebergQty?: number;

    /**
     * Supported values: [Time In Force](/products/spot/enums#timeinforce)
     * @type {'GTC' | 'IOC' | 'FOK'}
     * @memberof TradeApiOrderListPlaceOpo
     */
    readonly workingTimeInForce?: OrderListPlaceOpoWorkingTimeInForceEnum;

    /**
     * Arbitrary numeric value identifying the working order within an order strategy.
     * @type {number | bigint}
     * @memberof TradeApiOrderListPlaceOpo
     */
    readonly workingStrategyId?: number | bigint;

    /**
     * Arbitrary numeric value identifying the working order strategy. Values smaller than `1000000` are reserved and cannot be used.
     * @type {number}
     * @memberof TradeApiOrderListPlaceOpo
     */
    readonly workingStrategyType?: number;

    /**
     * See [Pegged Orders](/products/spot/faqs/pegged_orders)
     * @type {'PRIMARY_PEG' | 'MARKET_PEG'}
     * @memberof TradeApiOrderListPlaceOpo
     */
    readonly workingPegPriceType?: OrderListPlaceOpoWorkingPegPriceTypeEnum;

    /**
     *
     * @type {'PRICE_LEVEL'}
     * @memberof TradeApiOrderListPlaceOpo
     */
    readonly workingPegOffsetType?: OrderListPlaceOpoWorkingPegOffsetTypeEnum;

    /**
     *
     * @type {number}
     * @memberof TradeApiOrderListPlaceOpo
     */
    readonly workingPegOffsetValue?: number;

    /**
     * Arbitrary unique ID among open orders for the pending order. Automatically generated if not sent.
     * @type {string}
     * @memberof TradeApiOrderListPlaceOpo
     */
    readonly pendingClientOrderId?: string;

    /**
     * Price for the pending order.
     * @type {number}
     * @memberof TradeApiOrderListPlaceOpo
     */
    readonly pendingPrice?: number;

    /**
     * Stop price for the pending order.
     * @type {number}
     * @memberof TradeApiOrderListPlaceOpo
     */
    readonly pendingStopPrice?: number;

    /**
     * Trailing delta for the pending order.
     * @type {number}
     * @memberof TradeApiOrderListPlaceOpo
     */
    readonly pendingTrailingDelta?: number;

    /**
     * This can only be used if `pendingTimeInForce` is `GTC` or if `pendingType` is `LIMIT_MAKER`.
     * @type {number}
     * @memberof TradeApiOrderListPlaceOpo
     */
    readonly pendingIcebergQty?: number;

    /**
     * Supported values: [Time In Force](/products/spot/enums#timeinforce)
     * @type {'GTC' | 'IOC' | 'FOK'}
     * @memberof TradeApiOrderListPlaceOpo
     */
    readonly pendingTimeInForce?: OrderListPlaceOpoPendingTimeInForceEnum;

    /**
     * Arbitrary numeric value identifying the pending order within an order strategy.
     * @type {number | bigint}
     * @memberof TradeApiOrderListPlaceOpo
     */
    readonly pendingStrategyId?: number | bigint;

    /**
     * Arbitrary numeric value identifying the pending order strategy. Values smaller than `1000000` are reserved and cannot be used.
     * @type {number}
     * @memberof TradeApiOrderListPlaceOpo
     */
    readonly pendingStrategyType?: number;

    /**
     * See [Pegged Orders](/products/spot/faqs/pegged_orders)
     * @type {'PRIMARY_PEG' | 'MARKET_PEG'}
     * @memberof TradeApiOrderListPlaceOpo
     */
    readonly pendingPegPriceType?: OrderListPlaceOpoPendingPegPriceTypeEnum;

    /**
     *
     * @type {'PRICE_LEVEL'}
     * @memberof TradeApiOrderListPlaceOpo
     */
    readonly pendingPegOffsetType?: OrderListPlaceOpoPendingPegOffsetTypeEnum;

    /**
     *
     * @type {number}
     * @memberof TradeApiOrderListPlaceOpo
     */
    readonly pendingPegOffsetValue?: number;

    /**
     * Supports up to three decimal places of precision (e.g., 6000.346) so that microseconds may be specified.
     * @type {number}
     * @memberof TradeApiOrderListPlaceOpo
     */
    readonly recvWindow?: number;
}

/**
 * Request parameters for orderListPlaceOpoco operation in TradeApi.
 * @interface OrderListPlaceOpocoRequest
 */
export interface OrderListPlaceOpocoRequest {
    /**
     *
     * @type {string}
     * @memberof TradeApiOrderListPlaceOpoco
     */
    readonly symbol: string;

    /**
     *
     * @type {'LIMIT' | 'LIMIT_MAKER'}
     * @memberof TradeApiOrderListPlaceOpoco
     */
    readonly workingType: OrderListPlaceOpocoWorkingTypeEnum;

    /**
     * Supported values: [Order Side](/products/spot/enums#side)
     * @type {'BUY' | 'SELL'}
     * @memberof TradeApiOrderListPlaceOpoco
     */
    readonly workingSide: OrderListPlaceOpocoWorkingSideEnum;

    /**
     * Price for the working order.
     * @type {number}
     * @memberof TradeApiOrderListPlaceOpoco
     */
    readonly workingPrice: number;

    /**
     * Sets the quantity for the working order.
     * @type {number}
     * @memberof TradeApiOrderListPlaceOpoco
     */
    readonly workingQuantity: number;

    /**
     * Supported values: [Order Side](/products/spot/enums#side)
     * @type {'BUY' | 'SELL'}
     * @memberof TradeApiOrderListPlaceOpoco
     */
    readonly pendingSide: OrderListPlaceOpocoPendingSideEnum;

    /**
     * Supported values: `STOP_LOSS_LIMIT`, `STOP_LOSS`, `LIMIT_MAKER`, `TAKE_PROFIT`, `TAKE_PROFIT_LIMIT`
     * @type {'STOP_LOSS_LIMIT' | 'STOP_LOSS' | 'LIMIT_MAKER' | 'TAKE_PROFIT' | 'TAKE_PROFIT_LIMIT'}
     * @memberof TradeApiOrderListPlaceOpoco
     */
    readonly pendingAboveType: OrderListPlaceOpocoPendingAboveTypeEnum;

    /**
     * Client-generated request identifier.
     * @type {string}
     * @memberof TradeApiOrderListPlaceOpoco
     */
    readonly id?: string;

    /**
     * Arbitrary unique ID among open order lists. Automatically generated if not sent. A new order list with the same `listClientOrderId` is accepted only when the previous one is filled or completely expired. `listClientOrderId` is distinct from the `workingClientOrderId` and the `pendingClientOrderId`.
     * @type {string}
     * @memberof TradeApiOrderListPlaceOpoco
     */
    readonly listClientOrderId?: string;

    /**
     * Format of the JSON response. Supported values: [Order Response Type](/products/spot/enums#orderresponsetype)
     * @type {'ACK' | 'RESULT' | 'FULL'}
     * @memberof TradeApiOrderListPlaceOpoco
     */
    readonly newOrderRespType?: OrderListPlaceOpocoNewOrderRespTypeEnum;

    /**
     * The allowed enums is dependent on what is configured on the symbol. Supported values: [STP Modes](/products/spot/enums#stpmodes)
     * @type {'NONE' | 'EXPIRE_TAKER' | 'EXPIRE_MAKER' | 'EXPIRE_BOTH' | 'DECREMENT' | 'TRANSFER'}
     * @memberof TradeApiOrderListPlaceOpoco
     */
    readonly selfTradePreventionMode?: OrderListPlaceOpocoSelfTradePreventionModeEnum;

    /**
     * Arbitrary unique ID among open orders for the working order. Automatically generated if not sent.
     * @type {string}
     * @memberof TradeApiOrderListPlaceOpoco
     */
    readonly workingClientOrderId?: string;

    /**
     * This can only be used if `workingTimeInForce` is `GTC`, or if `workingType` is `LIMIT_MAKER`.
     * @type {number}
     * @memberof TradeApiOrderListPlaceOpoco
     */
    readonly workingIcebergQty?: number;

    /**
     * Supported values: [Time In Force](/products/spot/enums#timeinforce)
     * @type {'GTC' | 'IOC' | 'FOK'}
     * @memberof TradeApiOrderListPlaceOpoco
     */
    readonly workingTimeInForce?: OrderListPlaceOpocoWorkingTimeInForceEnum;

    /**
     * Arbitrary numeric value identifying the working order within an order strategy.
     * @type {number | bigint}
     * @memberof TradeApiOrderListPlaceOpoco
     */
    readonly workingStrategyId?: number | bigint;

    /**
     * Arbitrary numeric value identifying the working order strategy. Values smaller than `1000000` are reserved and cannot be used.
     * @type {number}
     * @memberof TradeApiOrderListPlaceOpoco
     */
    readonly workingStrategyType?: number;

    /**
     * See [Pegged Orders](/products/spot/faqs/pegged_orders)
     * @type {'PRIMARY_PEG' | 'MARKET_PEG'}
     * @memberof TradeApiOrderListPlaceOpoco
     */
    readonly workingPegPriceType?: OrderListPlaceOpocoWorkingPegPriceTypeEnum;

    /**
     * See [Pegged Orders](/products/spot/faqs/pegged_orders)
     * @type {'PRICE_LEVEL'}
     * @memberof TradeApiOrderListPlaceOpoco
     */
    readonly workingPegOffsetType?: OrderListPlaceOpocoWorkingPegOffsetTypeEnum;

    /**
     * Price level for pegging (max: 100). See [Pegged Orders](/products/spot/faqs/pegged_orders)
     * @type {number}
     * @memberof TradeApiOrderListPlaceOpoco
     */
    readonly workingPegOffsetValue?: number;

    /**
     * Arbitrary unique ID among open orders for the pending above order. Automatically generated if not sent.
     * @type {string}
     * @memberof TradeApiOrderListPlaceOpoco
     */
    readonly pendingAboveClientOrderId?: string;

    /**
     * Can be used if `pendingAboveType` is `STOP_LOSS_LIMIT`, `LIMIT_MAKER`, or `TAKE_PROFIT_LIMIT` to specify the limit price.
     * @type {number}
     * @memberof TradeApiOrderListPlaceOpoco
     */
    readonly pendingAbovePrice?: number;

    /**
     * Can be used if `pendingAboveType` is `STOP_LOSS`, `STOP_LOSS_LIMIT`, `TAKE_PROFIT`, `TAKE_PROFIT_LIMIT`.
     * @type {number}
     * @memberof TradeApiOrderListPlaceOpoco
     */
    readonly pendingAboveStopPrice?: number;

    /**
     * See [Trailing Stop order FAQ](/products/spot/faqs/trailing-stop-faq)
     * @type {number}
     * @memberof TradeApiOrderListPlaceOpoco
     */
    readonly pendingAboveTrailingDelta?: number;

    /**
     * This can only be used if `pendingAboveTimeInForce` is `GTC` or `pendingAboveType` is `LIMIT_MAKER`.
     * @type {number}
     * @memberof TradeApiOrderListPlaceOpoco
     */
    readonly pendingAboveIcebergQty?: number;

    /**
     * Required if `pendingAboveType` is `STOP_LOSS_LIMIT` or `TAKE_PROFIT_LIMIT`.
     * @type {'GTC' | 'IOC' | 'FOK'}
     * @memberof TradeApiOrderListPlaceOpoco
     */
    readonly pendingAboveTimeInForce?: OrderListPlaceOpocoPendingAboveTimeInForceEnum;

    /**
     * Arbitrary numeric value identifying the pending above order within an order strategy.
     * @type {number | bigint}
     * @memberof TradeApiOrderListPlaceOpoco
     */
    readonly pendingAboveStrategyId?: number | bigint;

    /**
     * Arbitrary numeric value identifying the pending above order strategy. Values smaller than `1000000` are reserved and cannot be used.
     * @type {number}
     * @memberof TradeApiOrderListPlaceOpoco
     */
    readonly pendingAboveStrategyType?: number;

    /**
     * See [Pegged Orders](/products/spot/faqs/pegged_orders)
     * @type {'PRIMARY_PEG' | 'MARKET_PEG'}
     * @memberof TradeApiOrderListPlaceOpoco
     */
    readonly pendingAbovePegPriceType?: OrderListPlaceOpocoPendingAbovePegPriceTypeEnum;

    /**
     * See [Pegged Orders](/products/spot/faqs/pegged_orders)
     * @type {'PRICE_LEVEL'}
     * @memberof TradeApiOrderListPlaceOpoco
     */
    readonly pendingAbovePegOffsetType?: OrderListPlaceOpocoPendingAbovePegOffsetTypeEnum;

    /**
     * Price level for pegging (max: 100). See [Pegged Orders](/products/spot/faqs/pegged_orders)
     * @type {number}
     * @memberof TradeApiOrderListPlaceOpoco
     */
    readonly pendingAbovePegOffsetValue?: number;

    /**
     * Supported values: `STOP_LOSS`, `STOP_LOSS_LIMIT`, `TAKE_PROFIT`, `TAKE_PROFIT_LIMIT`
     * @type {'STOP_LOSS' | 'STOP_LOSS_LIMIT' | 'TAKE_PROFIT' | 'TAKE_PROFIT_LIMIT'}
     * @memberof TradeApiOrderListPlaceOpoco
     */
    readonly pendingBelowType?: OrderListPlaceOpocoPendingBelowTypeEnum;

    /**
     * Arbitrary unique ID among open orders for the pending below order. Automatically generated if not sent.
     * @type {string}
     * @memberof TradeApiOrderListPlaceOpoco
     */
    readonly pendingBelowClientOrderId?: string;

    /**
     * Can be used if `pendingBelowType` is `STOP_LOSS_LIMIT` or `TAKE_PROFIT_LIMIT` to specify the limit price.
     * @type {number}
     * @memberof TradeApiOrderListPlaceOpoco
     */
    readonly pendingBelowPrice?: number;

    /**
     * Can be used if `pendingBelowType` is `STOP_LOSS`, `STOP_LOSS_LIMIT`, `TAKE_PROFIT`, `TAKE_PROFIT_LIMIT`. Either `pendingBelowStopPrice` or `pendingBelowTrailingDelta` or both, must be specified.
     * @type {number}
     * @memberof TradeApiOrderListPlaceOpoco
     */
    readonly pendingBelowStopPrice?: number;

    /**
     * See [Trailing Stop order FAQ](/products/spot/faqs/trailing-stop-faq)
     * @type {number}
     * @memberof TradeApiOrderListPlaceOpoco
     */
    readonly pendingBelowTrailingDelta?: number;

    /**
     * This can only be used if `pendingBelowTimeInForce` is `GTC` or `pendingBelowType` is `LIMIT_MAKER`.
     * @type {number}
     * @memberof TradeApiOrderListPlaceOpoco
     */
    readonly pendingBelowIcebergQty?: number;

    /**
     * Supported values: [Time In Force](/products/spot/enums#timeinforce)
     * @type {'GTC' | 'IOC' | 'FOK'}
     * @memberof TradeApiOrderListPlaceOpoco
     */
    readonly pendingBelowTimeInForce?: OrderListPlaceOpocoPendingBelowTimeInForceEnum;

    /**
     * Arbitrary numeric value identifying the pending below order within an order strategy.
     * @type {number | bigint}
     * @memberof TradeApiOrderListPlaceOpoco
     */
    readonly pendingBelowStrategyId?: number | bigint;

    /**
     * Arbitrary numeric value identifying the pending below order strategy. Values smaller than `1000000` are reserved and cannot be used.
     * @type {number}
     * @memberof TradeApiOrderListPlaceOpoco
     */
    readonly pendingBelowStrategyType?: number;

    /**
     * See [Pegged Orders](/products/spot/faqs/pegged_orders)
     * @type {'PRIMARY_PEG' | 'MARKET_PEG'}
     * @memberof TradeApiOrderListPlaceOpoco
     */
    readonly pendingBelowPegPriceType?: OrderListPlaceOpocoPendingBelowPegPriceTypeEnum;

    /**
     *
     * @type {'PRICE_LEVEL'}
     * @memberof TradeApiOrderListPlaceOpoco
     */
    readonly pendingBelowPegOffsetType?: OrderListPlaceOpocoPendingBelowPegOffsetTypeEnum;

    /**
     *
     * @type {number}
     * @memberof TradeApiOrderListPlaceOpoco
     */
    readonly pendingBelowPegOffsetValue?: number;

    /**
     * Supports up to three decimal places of precision (e.g., 6000.346) so that microseconds may be specified.
     * @type {number}
     * @memberof TradeApiOrderListPlaceOpoco
     */
    readonly recvWindow?: number;
}

/**
 * Request parameters for orderListPlaceOto operation in TradeApi.
 * @interface OrderListPlaceOtoRequest
 */
export interface OrderListPlaceOtoRequest {
    /**
     *
     * @type {string}
     * @memberof TradeApiOrderListPlaceOto
     */
    readonly symbol: string;

    /**
     * Supported values: `LIMIT`, `LIMIT_MAKER`
     * @type {'LIMIT' | 'LIMIT_MAKER'}
     * @memberof TradeApiOrderListPlaceOto
     */
    readonly workingType: OrderListPlaceOtoWorkingTypeEnum;

    /**
     * Supported values: [Order Side](/products/spot/enums#side)
     * @type {'BUY' | 'SELL'}
     * @memberof TradeApiOrderListPlaceOto
     */
    readonly workingSide: OrderListPlaceOtoWorkingSideEnum;

    /**
     *
     * @type {number}
     * @memberof TradeApiOrderListPlaceOto
     */
    readonly workingPrice: number;

    /**
     * Sets the quantity for the working order.
     * @type {number}
     * @memberof TradeApiOrderListPlaceOto
     */
    readonly workingQuantity: number;

    /**
     * Supported values: [Order Types](/products/spot/enums#ordertypes). Note that `MARKET` orders using `quoteOrderQty` are not supported.
     * @type {'LIMIT' | 'MARKET' | 'STOP_LOSS' | 'STOP_LOSS_LIMIT' | 'TAKE_PROFIT' | 'TAKE_PROFIT_LIMIT' | 'LIMIT_MAKER'}
     * @memberof TradeApiOrderListPlaceOto
     */
    readonly pendingType: OrderListPlaceOtoPendingTypeEnum;

    /**
     * Supported values: [Order Side](/products/spot/enums#side)
     * @type {'BUY' | 'SELL'}
     * @memberof TradeApiOrderListPlaceOto
     */
    readonly pendingSide: OrderListPlaceOtoPendingSideEnum;

    /**
     * Sets the quantity for the pending order.
     * @type {number}
     * @memberof TradeApiOrderListPlaceOto
     */
    readonly pendingQuantity: number;

    /**
     * Client-generated request identifier.
     * @type {string}
     * @memberof TradeApiOrderListPlaceOto
     */
    readonly id?: string;

    /**
     * Arbitrary unique ID among open order lists. Automatically generated if not sent. A new order list with the same `listClientOrderId` is accepted only when the previous one is filled or completely expired. `listClientOrderId` is distinct from the `workingClientOrderId` and the `pendingClientOrderId`.
     * @type {string}
     * @memberof TradeApiOrderListPlaceOto
     */
    readonly listClientOrderId?: string;

    /**
     * Format of the JSON response. Supported values: [Order Response Type](/products/spot/enums#orderresponsetype)
     * @type {'ACK' | 'RESULT' | 'FULL'}
     * @memberof TradeApiOrderListPlaceOto
     */
    readonly newOrderRespType?: OrderListPlaceOtoNewOrderRespTypeEnum;

    /**
     * The allowed enums is dependent on what is configured on the symbol. Supported values: [STP Modes](/products/spot/enums#stpmodes)
     * @type {'NONE' | 'EXPIRE_TAKER' | 'EXPIRE_MAKER' | 'EXPIRE_BOTH' | 'DECREMENT' | 'TRANSFER'}
     * @memberof TradeApiOrderListPlaceOto
     */
    readonly selfTradePreventionMode?: OrderListPlaceOtoSelfTradePreventionModeEnum;

    /**
     * Arbitrary unique ID among open orders for the working order. Automatically generated if not sent.
     * @type {string}
     * @memberof TradeApiOrderListPlaceOto
     */
    readonly workingClientOrderId?: string;

    /**
     * This can only be used if `workingTimeInForce` is `GTC`, or if `workingType` is `LIMIT_MAKER`.
     * @type {number}
     * @memberof TradeApiOrderListPlaceOto
     */
    readonly workingIcebergQty?: number;

    /**
     * Supported values: [Time In Force](/products/spot/enums#timeinforce)
     * @type {'GTC' | 'IOC' | 'FOK'}
     * @memberof TradeApiOrderListPlaceOto
     */
    readonly workingTimeInForce?: OrderListPlaceOtoWorkingTimeInForceEnum;

    /**
     * Arbitrary numeric value identifying the working order within an order strategy.
     * @type {number | bigint}
     * @memberof TradeApiOrderListPlaceOto
     */
    readonly workingStrategyId?: number | bigint;

    /**
     * Arbitrary numeric value identifying the working order strategy. Values smaller than `1000000` are reserved and cannot be used.
     * @type {number}
     * @memberof TradeApiOrderListPlaceOto
     */
    readonly workingStrategyType?: number;

    /**
     * See [Pegged Orders](/products/spot/faqs/pegged_orders)
     * @type {'PRIMARY_PEG' | 'MARKET_PEG'}
     * @memberof TradeApiOrderListPlaceOto
     */
    readonly workingPegPriceType?: OrderListPlaceOtoWorkingPegPriceTypeEnum;

    /**
     *
     * @type {'PRICE_LEVEL'}
     * @memberof TradeApiOrderListPlaceOto
     */
    readonly workingPegOffsetType?: OrderListPlaceOtoWorkingPegOffsetTypeEnum;

    /**
     *
     * @type {number}
     * @memberof TradeApiOrderListPlaceOto
     */
    readonly workingPegOffsetValue?: number;

    /**
     * Arbitrary unique ID among open orders for the pending order. Automatically generated if not sent.
     * @type {string}
     * @memberof TradeApiOrderListPlaceOto
     */
    readonly pendingClientOrderId?: string;

    /**
     *
     * @type {number}
     * @memberof TradeApiOrderListPlaceOto
     */
    readonly pendingPrice?: number;

    /**
     *
     * @type {number}
     * @memberof TradeApiOrderListPlaceOto
     */
    readonly pendingStopPrice?: number;

    /**
     *
     * @type {number}
     * @memberof TradeApiOrderListPlaceOto
     */
    readonly pendingTrailingDelta?: number;

    /**
     * This can only be used if `pendingTimeInForce` is `GTC` or if `pendingType` is `LIMIT_MAKER`.
     * @type {number}
     * @memberof TradeApiOrderListPlaceOto
     */
    readonly pendingIcebergQty?: number;

    /**
     * Supported values: [Time In Force](/products/spot/enums#timeinforce)
     * @type {'GTC' | 'IOC' | 'FOK'}
     * @memberof TradeApiOrderListPlaceOto
     */
    readonly pendingTimeInForce?: OrderListPlaceOtoPendingTimeInForceEnum;

    /**
     * Arbitrary numeric value identifying the pending order within an order strategy.
     * @type {number | bigint}
     * @memberof TradeApiOrderListPlaceOto
     */
    readonly pendingStrategyId?: number | bigint;

    /**
     * Arbitrary numeric value identifying the pending order strategy. Values smaller than `1000000` are reserved and cannot be used.
     * @type {number}
     * @memberof TradeApiOrderListPlaceOto
     */
    readonly pendingStrategyType?: number;

    /**
     *
     * @type {'PRICE_LEVEL'}
     * @memberof TradeApiOrderListPlaceOto
     */
    readonly pendingPegOffsetType?: OrderListPlaceOtoPendingPegOffsetTypeEnum;

    /**
     * See [Pegged Orders](/products/spot/faqs/pegged_orders)
     * @type {'PRIMARY_PEG' | 'MARKET_PEG'}
     * @memberof TradeApiOrderListPlaceOto
     */
    readonly pendingPegPriceType?: OrderListPlaceOtoPendingPegPriceTypeEnum;

    /**
     *
     * @type {number}
     * @memberof TradeApiOrderListPlaceOto
     */
    readonly pendingPegOffsetValue?: number;

    /**
     * Supports up to three decimal places of precision (e.g., 6000.346) so that microseconds may be specified.
     * @type {number}
     * @memberof TradeApiOrderListPlaceOto
     */
    readonly recvWindow?: number;
}

/**
 * Request parameters for orderListPlaceOtoco operation in TradeApi.
 * @interface OrderListPlaceOtocoRequest
 */
export interface OrderListPlaceOtocoRequest {
    /**
     *
     * @type {string}
     * @memberof TradeApiOrderListPlaceOtoco
     */
    readonly symbol: string;

    /**
     * Supported values: `LIMIT`, `LIMIT_MAKER`
     * @type {'LIMIT' | 'LIMIT_MAKER'}
     * @memberof TradeApiOrderListPlaceOtoco
     */
    readonly workingType: OrderListPlaceOtocoWorkingTypeEnum;

    /**
     * Supported values: [Order Side](/products/spot/enums#side)
     * @type {'BUY' | 'SELL'}
     * @memberof TradeApiOrderListPlaceOtoco
     */
    readonly workingSide: OrderListPlaceOtocoWorkingSideEnum;

    /**
     *
     * @type {number}
     * @memberof TradeApiOrderListPlaceOtoco
     */
    readonly workingPrice: number;

    /**
     * Sets the quantity for the working order.
     * @type {number}
     * @memberof TradeApiOrderListPlaceOtoco
     */
    readonly workingQuantity: number;

    /**
     * Supported values: [Order Side](/products/spot/enums#side)
     * @type {'BUY' | 'SELL'}
     * @memberof TradeApiOrderListPlaceOtoco
     */
    readonly pendingSide: OrderListPlaceOtocoPendingSideEnum;

    /**
     * Sets the quantity for the pending orders.
     * @type {number}
     * @memberof TradeApiOrderListPlaceOtoco
     */
    readonly pendingQuantity: number;

    /**
     * Supported values: `STOP_LOSS_LIMIT`, `STOP_LOSS`, `LIMIT_MAKER`, `TAKE_PROFIT`, `TAKE_PROFIT_LIMIT`
     * @type {'STOP_LOSS_LIMIT' | 'STOP_LOSS' | 'LIMIT_MAKER' | 'TAKE_PROFIT' | 'TAKE_PROFIT_LIMIT'}
     * @memberof TradeApiOrderListPlaceOtoco
     */
    readonly pendingAboveType: OrderListPlaceOtocoPendingAboveTypeEnum;

    /**
     * Client-generated request identifier.
     * @type {string}
     * @memberof TradeApiOrderListPlaceOtoco
     */
    readonly id?: string;

    /**
     * Arbitrary unique ID among open order lists. Automatically generated if not sent. A new order list with the same `listClientOrderId` is accepted only when the previous one is filled or completely expired. `listClientOrderId` is distinct from the `workingClientOrderId` and the `pendingClientOrderId`.
     * @type {string}
     * @memberof TradeApiOrderListPlaceOtoco
     */
    readonly listClientOrderId?: string;

    /**
     * Format of the JSON response. Supported values: [Order Response Type](/products/spot/enums#orderresponsetype)
     * @type {'ACK' | 'RESULT' | 'FULL'}
     * @memberof TradeApiOrderListPlaceOtoco
     */
    readonly newOrderRespType?: OrderListPlaceOtocoNewOrderRespTypeEnum;

    /**
     * The allowed enums is dependent on what is configured on the symbol. Supported values: [STP Modes](/products/spot/enums#stpmodes)
     * @type {'NONE' | 'EXPIRE_TAKER' | 'EXPIRE_MAKER' | 'EXPIRE_BOTH' | 'DECREMENT' | 'TRANSFER'}
     * @memberof TradeApiOrderListPlaceOtoco
     */
    readonly selfTradePreventionMode?: OrderListPlaceOtocoSelfTradePreventionModeEnum;

    /**
     * Arbitrary unique ID among open orders for the working order. Automatically generated if not sent.
     * @type {string}
     * @memberof TradeApiOrderListPlaceOtoco
     */
    readonly workingClientOrderId?: string;

    /**
     * This can only be used if `workingTimeInForce` is `GTC`, or if `workingType` is `LIMIT_MAKER`.
     * @type {number}
     * @memberof TradeApiOrderListPlaceOtoco
     */
    readonly workingIcebergQty?: number;

    /**
     * Supported values: [Time In Force](/products/spot/enums#timeinforce)
     * @type {'GTC' | 'IOC' | 'FOK'}
     * @memberof TradeApiOrderListPlaceOtoco
     */
    readonly workingTimeInForce?: OrderListPlaceOtocoWorkingTimeInForceEnum;

    /**
     * Arbitrary numeric value identifying the working order within an order strategy.
     * @type {number | bigint}
     * @memberof TradeApiOrderListPlaceOtoco
     */
    readonly workingStrategyId?: number | bigint;

    /**
     * Arbitrary numeric value identifying the working order strategy. Values smaller than `1000000` are reserved and cannot be used.
     * @type {number}
     * @memberof TradeApiOrderListPlaceOtoco
     */
    readonly workingStrategyType?: number;

    /**
     * See [Pegged Orders](/products/spot/faqs/pegged_orders)
     * @type {'PRIMARY_PEG' | 'MARKET_PEG'}
     * @memberof TradeApiOrderListPlaceOtoco
     */
    readonly workingPegPriceType?: OrderListPlaceOtocoWorkingPegPriceTypeEnum;

    /**
     *
     * @type {'PRICE_LEVEL'}
     * @memberof TradeApiOrderListPlaceOtoco
     */
    readonly workingPegOffsetType?: OrderListPlaceOtocoWorkingPegOffsetTypeEnum;

    /**
     *
     * @type {number}
     * @memberof TradeApiOrderListPlaceOtoco
     */
    readonly workingPegOffsetValue?: number;

    /**
     * Arbitrary unique ID among open orders for the pending above order. Automatically generated if not sent.
     * @type {string}
     * @memberof TradeApiOrderListPlaceOtoco
     */
    readonly pendingAboveClientOrderId?: string;

    /**
     * Can be used if `pendingAboveType` is `STOP_LOSS_LIMIT`, `LIMIT_MAKER`, or `TAKE_PROFIT_LIMIT` to specify the limit price.
     * @type {number}
     * @memberof TradeApiOrderListPlaceOtoco
     */
    readonly pendingAbovePrice?: number;

    /**
     * Can be used if `pendingAboveType` is `STOP_LOSS`, `STOP_LOSS_LIMIT`, `TAKE_PROFIT`, `TAKE_PROFIT_LIMIT`.
     * @type {number}
     * @memberof TradeApiOrderListPlaceOtoco
     */
    readonly pendingAboveStopPrice?: number;

    /**
     * See [Trailing Stop order FAQ](/products/spot/faqs/trailing-stop-faq)
     * @type {number}
     * @memberof TradeApiOrderListPlaceOtoco
     */
    readonly pendingAboveTrailingDelta?: number;

    /**
     * This can only be used if `pendingAboveTimeInForce` is `GTC` or if `pendingAboveType` is `LIMIT_MAKER`.
     * @type {number}
     * @memberof TradeApiOrderListPlaceOtoco
     */
    readonly pendingAboveIcebergQty?: number;

    /**
     * Required if `pendingAboveType` is `STOP_LOSS_LIMIT` or `TAKE_PROFIT_LIMIT`.
     * @type {'GTC' | 'IOC' | 'FOK'}
     * @memberof TradeApiOrderListPlaceOtoco
     */
    readonly pendingAboveTimeInForce?: OrderListPlaceOtocoPendingAboveTimeInForceEnum;

    /**
     * Arbitrary numeric value identifying the pending above order within an order strategy.
     * @type {number | bigint}
     * @memberof TradeApiOrderListPlaceOtoco
     */
    readonly pendingAboveStrategyId?: number | bigint;

    /**
     * Arbitrary numeric value identifying the pending above order strategy. Values smaller than `1000000` are reserved and cannot be used.
     * @type {number}
     * @memberof TradeApiOrderListPlaceOtoco
     */
    readonly pendingAboveStrategyType?: number;

    /**
     * See [Pegged Orders](/products/spot/faqs/pegged_orders)
     * @type {'PRIMARY_PEG' | 'MARKET_PEG'}
     * @memberof TradeApiOrderListPlaceOtoco
     */
    readonly pendingAbovePegPriceType?: OrderListPlaceOtocoPendingAbovePegPriceTypeEnum;

    /**
     *
     * @type {'PRICE_LEVEL'}
     * @memberof TradeApiOrderListPlaceOtoco
     */
    readonly pendingAbovePegOffsetType?: OrderListPlaceOtocoPendingAbovePegOffsetTypeEnum;

    /**
     *
     * @type {number}
     * @memberof TradeApiOrderListPlaceOtoco
     */
    readonly pendingAbovePegOffsetValue?: number;

    /**
     * Supported values: `STOP_LOSS`, `STOP_LOSS_LIMIT`, `TAKE_PROFIT`, `TAKE_PROFIT_LIMIT`
     * @type {'STOP_LOSS' | 'STOP_LOSS_LIMIT' | 'TAKE_PROFIT' | 'TAKE_PROFIT_LIMIT'}
     * @memberof TradeApiOrderListPlaceOtoco
     */
    readonly pendingBelowType?: OrderListPlaceOtocoPendingBelowTypeEnum;

    /**
     * Arbitrary unique ID among open orders for the pending below order. Automatically generated if not sent.
     * @type {string}
     * @memberof TradeApiOrderListPlaceOtoco
     */
    readonly pendingBelowClientOrderId?: string;

    /**
     * Can be used if `pendingBelowType` is `STOP_LOSS_LIMIT` or `TAKE_PROFIT_LIMIT` to specify the limit price.
     * @type {number}
     * @memberof TradeApiOrderListPlaceOtoco
     */
    readonly pendingBelowPrice?: number;

    /**
     * Can be used if `pendingBelowType` is `STOP_LOSS`, `STOP_LOSS_LIMIT`, `TAKE_PROFIT`, `TAKE_PROFIT_LIMIT`. Either `pendingBelowStopPrice` or `pendingBelowTrailingDelta` or both, must be specified.
     * @type {number}
     * @memberof TradeApiOrderListPlaceOtoco
     */
    readonly pendingBelowStopPrice?: number;

    /**
     * See [Trailing Stop order FAQ](/products/spot/faqs/trailing-stop-faq)
     * @type {number}
     * @memberof TradeApiOrderListPlaceOtoco
     */
    readonly pendingBelowTrailingDelta?: number;

    /**
     * This can only be used if `pendingBelowTimeInForce` is `GTC`, or if `pendingBelowType` is `LIMIT_MAKER`.
     * @type {number}
     * @memberof TradeApiOrderListPlaceOtoco
     */
    readonly pendingBelowIcebergQty?: number;

    /**
     * Required if `pendingBelowType` is `STOP_LOSS_LIMIT` or `TAKE_PROFIT_LIMIT`.
     * @type {'GTC' | 'IOC' | 'FOK'}
     * @memberof TradeApiOrderListPlaceOtoco
     */
    readonly pendingBelowTimeInForce?: OrderListPlaceOtocoPendingBelowTimeInForceEnum;

    /**
     * Arbitrary numeric value identifying the pending below order within an order strategy.
     * @type {number | bigint}
     * @memberof TradeApiOrderListPlaceOtoco
     */
    readonly pendingBelowStrategyId?: number | bigint;

    /**
     * Arbitrary numeric value identifying the pending below order strategy. Values smaller than `1000000` are reserved and cannot be used.
     * @type {number}
     * @memberof TradeApiOrderListPlaceOtoco
     */
    readonly pendingBelowStrategyType?: number;

    /**
     * See [Pegged Orders](/products/spot/faqs/pegged_orders)
     * @type {'PRIMARY_PEG' | 'MARKET_PEG'}
     * @memberof TradeApiOrderListPlaceOtoco
     */
    readonly pendingBelowPegPriceType?: OrderListPlaceOtocoPendingBelowPegPriceTypeEnum;

    /**
     *
     * @type {'PRICE_LEVEL'}
     * @memberof TradeApiOrderListPlaceOtoco
     */
    readonly pendingBelowPegOffsetType?: OrderListPlaceOtocoPendingBelowPegOffsetTypeEnum;

    /**
     *
     * @type {number}
     * @memberof TradeApiOrderListPlaceOtoco
     */
    readonly pendingBelowPegOffsetValue?: number;

    /**
     * Supports up to three decimal places of precision (e.g., 6000.346) so that microseconds may be specified.
     * @type {number}
     * @memberof TradeApiOrderListPlaceOtoco
     */
    readonly recvWindow?: number;
}

/**
 * Request parameters for orderPlace operation in TradeApi.
 * @interface OrderPlaceRequest
 */
export interface OrderPlaceRequest {
    /**
     *
     * @type {string}
     * @memberof TradeApiOrderPlace
     */
    readonly symbol: string;

    /**
     * Please see [Enums](/products/spot/enums#side) for supported values.
     * @type {'BUY' | 'SELL'}
     * @memberof TradeApiOrderPlace
     */
    readonly side: OrderPlaceSideEnum;

    /**
     * Please see [Enums](/products/spot/enums#ordertypes) for supported values.
     * @type {'MARKET' | 'LIMIT' | 'STOP_LOSS' | 'STOP_LOSS_LIMIT' | 'TAKE_PROFIT' | 'TAKE_PROFIT_LIMIT' | 'LIMIT_MAKER'}
     * @memberof TradeApiOrderPlace
     */
    readonly type: OrderPlaceTypeEnum;

    /**
     * Client-generated request identifier.
     * @type {string}
     * @memberof TradeApiOrderPlace
     */
    readonly id?: string;

    /**
     * Please see [Enums](/products/spot/enums#timeinforce) for supported values.
     * @type {'GTC' | 'IOC' | 'FOK'}
     * @memberof TradeApiOrderPlace
     */
    readonly timeInForce?: OrderPlaceTimeInForceEnum;

    /**
     *
     * @type {number}
     * @memberof TradeApiOrderPlace
     */
    readonly price?: number;

    /**
     *
     * @type {number}
     * @memberof TradeApiOrderPlace
     */
    readonly quantity?: number;

    /**
     *
     * @type {number}
     * @memberof TradeApiOrderPlace
     */
    readonly quoteOrderQty?: number;

    /**
     * A unique id among open orders. Automatically generated if not sent.<br/> Orders with the same `newClientOrderID` can be accepted only when the previous one is filled, otherwise the order will be rejected.
     * @type {string}
     * @memberof TradeApiOrderPlace
     */
    readonly newClientOrderId?: string;

    /**
     * `MARKET` and `LIMIT` order types default to `FULL`, all other orders default to `ACK`.
     * @type {'ACK' | 'RESULT' | 'FULL'}
     * @memberof TradeApiOrderPlace
     */
    readonly newOrderRespType?: OrderPlaceNewOrderRespTypeEnum;

    /**
     * Used with `STOP_LOSS`, `STOP_LOSS_LIMIT`, `TAKE_PROFIT`, and `TAKE_PROFIT_LIMIT` orders.
     * @type {number}
     * @memberof TradeApiOrderPlace
     */
    readonly stopPrice?: number;

    /**
     * See Trailing Stop order FAQ
     * @type {number}
     * @memberof TradeApiOrderPlace
     */
    readonly trailingDelta?: number;

    /**
     * Used with `LIMIT`, `STOP_LOSS_LIMIT`, and `TAKE_PROFIT_LIMIT` to create an iceberg order.
     * @type {number}
     * @memberof TradeApiOrderPlace
     */
    readonly icebergQty?: number;

    /**
     *
     * @type {number | bigint}
     * @memberof TradeApiOrderPlace
     */
    readonly strategyId?: number | bigint;

    /**
     * The value cannot be less than `1000000`.
     * @type {number}
     * @memberof TradeApiOrderPlace
     */
    readonly strategyType?: number;

    /**
     * The allowed enums is dependent on what is configured on the symbol.
     * @type {'NONE' | 'EXPIRE_TAKER' | 'EXPIRE_MAKER' | 'EXPIRE_BOTH' | 'DECREMENT' | 'TRANSFER'}
     * @memberof TradeApiOrderPlace
     */
    readonly selfTradePreventionMode?: OrderPlaceSelfTradePreventionModeEnum;

    /**
     * See Pegged Orders Info
     * @type {'PRIMARY_PEG' | 'MARKET_PEG'}
     * @memberof TradeApiOrderPlace
     */
    readonly pegPriceType?: OrderPlacePegPriceTypeEnum;

    /**
     * Price level to peg the price to (max: 100). See Pegged Orders Info
     * @type {number}
     * @memberof TradeApiOrderPlace
     */
    readonly pegOffsetValue?: number;

    /**
     * Only `PRICE_LEVEL` is supported. See Pegged Orders Info
     * @type {'PRICE_LEVEL'}
     * @memberof TradeApiOrderPlace
     */
    readonly pegOffsetType?: OrderPlacePegOffsetTypeEnum;

    /**
     * Supports up to three decimal places of precision (e.g., 6000.346) so that microseconds may be specified.
     * @type {number}
     * @memberof TradeApiOrderPlace
     */
    readonly recvWindow?: number;
}

/**
 * Request parameters for orderTest operation in TradeApi.
 * @interface OrderTestRequest
 */
export interface OrderTestRequest {
    /**
     *
     * @type {string}
     * @memberof TradeApiOrderTest
     */
    readonly symbol: string;

    /**
     * Please see [Enums](/products/spot/enums#side) for supported values.
     * @type {'BUY' | 'SELL'}
     * @memberof TradeApiOrderTest
     */
    readonly side: OrderTestSideEnum;

    /**
     * Please see [Enums](/products/spot/enums#ordertypes) for supported values.
     * @type {'MARKET' | 'LIMIT' | 'STOP_LOSS' | 'STOP_LOSS_LIMIT' | 'TAKE_PROFIT' | 'TAKE_PROFIT_LIMIT' | 'LIMIT_MAKER'}
     * @memberof TradeApiOrderTest
     */
    readonly type: OrderTestTypeEnum;

    /**
     * Client-generated request identifier.
     * @type {string}
     * @memberof TradeApiOrderTest
     */
    readonly id?: string;

    /**
     * Default: `false` <br> See [Commissions FAQ](/products/spot/faqs/commission_faq#test-order-diferences) to learn more.
     * @type {boolean}
     * @memberof TradeApiOrderTest
     */
    readonly computeCommissionRates?: boolean;

    /**
     * Please see [Enums](/products/spot/enums#timeinforce) for supported values.
     * @type {'GTC' | 'IOC' | 'FOK'}
     * @memberof TradeApiOrderTest
     */
    readonly timeInForce?: OrderTestTimeInForceEnum;

    /**
     *
     * @type {number}
     * @memberof TradeApiOrderTest
     */
    readonly price?: number;

    /**
     *
     * @type {number}
     * @memberof TradeApiOrderTest
     */
    readonly quantity?: number;

    /**
     *
     * @type {number}
     * @memberof TradeApiOrderTest
     */
    readonly quoteOrderQty?: number;

    /**
     * A unique id among open orders. Automatically generated if not sent. Orders with the same `newClientOrderID` can be accepted only when the previous one is filled, otherwise the order will be rejected.
     * @type {string}
     * @memberof TradeApiOrderTest
     */
    readonly newClientOrderId?: string;

    /**
     * Set the response JSON. `ACK`, `RESULT`, or `FULL`; `MARKET` and `LIMIT` order types default to `FULL`, all other orders default to `ACK`.
     * @type {'ACK' | 'RESULT' | 'FULL'}
     * @memberof TradeApiOrderTest
     */
    readonly newOrderRespType?: OrderTestNewOrderRespTypeEnum;

    /**
     * Used with `STOP_LOSS`, `STOP_LOSS_LIMIT`, `TAKE_PROFIT`, and `TAKE_PROFIT_LIMIT` orders.
     * @type {number}
     * @memberof TradeApiOrderTest
     */
    readonly stopPrice?: number;

    /**
     * See [Trailing Stop order FAQ](/products/spot/faqs/trailing-stop-faq)
     * @type {number}
     * @memberof TradeApiOrderTest
     */
    readonly trailingDelta?: number;

    /**
     * Used with `LIMIT`, `STOP_LOSS_LIMIT`, and `TAKE_PROFIT_LIMIT` to create an iceberg order.
     * @type {number}
     * @memberof TradeApiOrderTest
     */
    readonly icebergQty?: number;

    /**
     *
     * @type {number | bigint}
     * @memberof TradeApiOrderTest
     */
    readonly strategyId?: number | bigint;

    /**
     * The value cannot be less than `1000000`.
     * @type {number}
     * @memberof TradeApiOrderTest
     */
    readonly strategyType?: number;

    /**
     * The allowed enums is dependent on what is configured on the symbol. Supported values: [STP Modes](/products/spot/enums#stpmodes)
     * @type {'NONE' | 'EXPIRE_TAKER' | 'EXPIRE_MAKER' | 'EXPIRE_BOTH' | 'DECREMENT' | 'TRANSFER'}
     * @memberof TradeApiOrderTest
     */
    readonly selfTradePreventionMode?: OrderTestSelfTradePreventionModeEnum;

    /**
     * `PRIMARY_PEG` or `MARKET_PEG`. See [Pegged Orders](/products/spot/faqs/pegged_orders)
     * @type {'PRIMARY_PEG' | 'MARKET_PEG'}
     * @memberof TradeApiOrderTest
     */
    readonly pegPriceType?: OrderTestPegPriceTypeEnum;

    /**
     * Price level for pegging (max: 100). See [Pegged Orders](/products/spot/faqs/pegged_orders)
     * @type {number}
     * @memberof TradeApiOrderTest
     */
    readonly pegOffsetValue?: number;

    /**
     * Only `PRICE_LEVEL` is supported. See [Pegged Orders](/products/spot/faqs/pegged_orders)
     * @type {'PRICE_LEVEL'}
     * @memberof TradeApiOrderTest
     */
    readonly pegOffsetType?: OrderTestPegOffsetTypeEnum;

    /**
     * Supports up to three decimal places of precision (e.g., 6000.346) so that microseconds may be specified.
     * @type {number}
     * @memberof TradeApiOrderTest
     */
    readonly recvWindow?: number;
}

/**
 * Request parameters for sorOrderPlace operation in TradeApi.
 * @interface SorOrderPlaceRequest
 */
export interface SorOrderPlaceRequest {
    /**
     *
     * @type {string}
     * @memberof TradeApiSorOrderPlace
     */
    readonly symbol: string;

    /**
     * `BUY` or `SELL`
     * @type {'BUY' | 'SELL'}
     * @memberof TradeApiSorOrderPlace
     */
    readonly side: SorOrderPlaceSideEnum;

    /**
     * Only `LIMIT` and `MARKET` orders are supported.
     * @type {'MARKET' | 'LIMIT'}
     * @memberof TradeApiSorOrderPlace
     */
    readonly type: SorOrderPlaceTypeEnum;

    /**
     *
     * @type {number}
     * @memberof TradeApiSorOrderPlace
     */
    readonly quantity: number;

    /**
     * Client-generated request identifier.
     * @type {string}
     * @memberof TradeApiSorOrderPlace
     */
    readonly id?: string;

    /**
     * Applicable only to `LIMIT` order type.
     * @type {'GTC' | 'IOC' | 'FOK'}
     * @memberof TradeApiSorOrderPlace
     */
    readonly timeInForce?: SorOrderPlaceTimeInForceEnum;

    /**
     *
     * @type {number}
     * @memberof TradeApiSorOrderPlace
     */
    readonly price?: number;

    /**
     * A unique id among open orders. Automatically generated if not sent.<br/> Orders with the same `newClientOrderID` can be accepted only when the previous one is filled, otherwise the order will be rejected.
     * @type {string}
     * @memberof TradeApiSorOrderPlace
     */
    readonly newClientOrderId?: string;

    /**
     * Set the response JSON. `ACK`, `RESULT`, or `FULL`. Default to `FULL`
     * @type {'ACK' | 'RESULT' | 'FULL'}
     * @memberof TradeApiSorOrderPlace
     */
    readonly newOrderRespType?: SorOrderPlaceNewOrderRespTypeEnum;

    /**
     * Used with `LIMIT` to create an iceberg order.
     * @type {number}
     * @memberof TradeApiSorOrderPlace
     */
    readonly icebergQty?: number;

    /**
     *
     * @type {number | bigint}
     * @memberof TradeApiSorOrderPlace
     */
    readonly strategyId?: number | bigint;

    /**
     * The value cannot be less than `1000000`.
     * @type {number}
     * @memberof TradeApiSorOrderPlace
     */
    readonly strategyType?: number;

    /**
     * The allowed enums is dependent on what is configured on the symbol. The possible supported values are: [STP Modes](/products/spot/enums#stpmodes).
     * @type {'NONE' | 'EXPIRE_TAKER' | 'EXPIRE_MAKER' | 'EXPIRE_BOTH' | 'DECREMENT' | 'TRANSFER'}
     * @memberof TradeApiSorOrderPlace
     */
    readonly selfTradePreventionMode?: SorOrderPlaceSelfTradePreventionModeEnum;

    /**
     * Supports up to three decimal places of precision (e.g., 6000.346) so that microseconds may be specified.
     * @type {number}
     * @memberof TradeApiSorOrderPlace
     */
    readonly recvWindow?: number;
}

/**
 * Request parameters for sorOrderTest operation in TradeApi.
 * @interface SorOrderTestRequest
 */
export interface SorOrderTestRequest {
    /**
     *
     * @type {string}
     * @memberof TradeApiSorOrderTest
     */
    readonly symbol: string;

    /**
     * Please see [Enums](/products/spot/enums#side) for supported values.
     * @type {'BUY' | 'SELL'}
     * @memberof TradeApiSorOrderTest
     */
    readonly side: SorOrderTestSideEnum;

    /**
     * Please see [Enums](/products/spot/enums#ordertypes) for supported values.
     * @type {'MARKET' | 'LIMIT'}
     * @memberof TradeApiSorOrderTest
     */
    readonly type: SorOrderTestTypeEnum;

    /**
     *
     * @type {number}
     * @memberof TradeApiSorOrderTest
     */
    readonly quantity: number;

    /**
     * Client-generated request identifier.
     * @type {string}
     * @memberof TradeApiSorOrderTest
     */
    readonly id?: string;

    /**
     * Default: `false`
     * @type {boolean}
     * @memberof TradeApiSorOrderTest
     */
    readonly computeCommissionRates?: boolean;

    /**
     * Please see [Enums](/products/spot/enums#timeinforce) for supported values.
     * @type {'GTC' | 'IOC' | 'FOK'}
     * @memberof TradeApiSorOrderTest
     */
    readonly timeInForce?: SorOrderTestTimeInForceEnum;

    /**
     *
     * @type {number}
     * @memberof TradeApiSorOrderTest
     */
    readonly price?: number;

    /**
     * A unique id among open orders. Automatically generated if not sent. Orders with the same `newClientOrderID` can be accepted only when the previous one is filled, otherwise the order will be rejected.
     * @type {string}
     * @memberof TradeApiSorOrderTest
     */
    readonly newClientOrderId?: string;

    /**
     * Set the response JSON. `ACK`, `RESULT`, or `FULL`. Default to `FULL`.
     * @type {'ACK' | 'RESULT' | 'FULL'}
     * @memberof TradeApiSorOrderTest
     */
    readonly newOrderRespType?: SorOrderTestNewOrderRespTypeEnum;

    /**
     * Used with `LIMIT` to create an iceberg order.
     * @type {number}
     * @memberof TradeApiSorOrderTest
     */
    readonly icebergQty?: number;

    /**
     *
     * @type {number | bigint}
     * @memberof TradeApiSorOrderTest
     */
    readonly strategyId?: number | bigint;

    /**
     * The value cannot be less than `1000000`.
     * @type {number}
     * @memberof TradeApiSorOrderTest
     */
    readonly strategyType?: number;

    /**
     * The allowed enums is dependent on what is configured on the symbol. Supported values: [STP Modes](/products/spot/enums#stpmodes)
     * @type {'NONE' | 'EXPIRE_TAKER' | 'EXPIRE_MAKER' | 'EXPIRE_BOTH' | 'DECREMENT' | 'TRANSFER'}
     * @memberof TradeApiSorOrderTest
     */
    readonly selfTradePreventionMode?: SorOrderTestSelfTradePreventionModeEnum;

    /**
     * Supports up to three decimal places of precision (e.g., 6000.346) so that microseconds may be specified.
     * @type {number}
     * @memberof TradeApiSorOrderTest
     */
    readonly recvWindow?: number;
}

/**
 * TradeApi - object-oriented interface
 * @class TradeApi
 * @extends {WebsocketAPIBase}
 */
export class TradeApi implements TradeApiInterface {
    private readonly websocketBase: WebsocketAPIBase;

    constructor(websocketBase: WebsocketAPIBase) {
        this.websocketBase = websocketBase;
    }

    /**
     * Cancel all open orders on a symbol.
     * This includes orders that are part of an order list.
     *
     * Weight(IP): 1
     *
     * Security Type: TRADE
     *
     * Notes:
     **Data Source:** Matching Engine
     *
     * @summary Cancel open orders (TRADE)
     * @param {OpenOrdersCancelAllRequest} requestParameters Request parameters.
     * @returns {Promise<OpenOrdersCancelAllResponse>}
     * @memberof TradeApi
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-spot-trading/api/ws-api/trade#open-orders-cancel-all Binance API Documentation}
     */
    public openOrdersCancelAll(
        requestParameters: OpenOrdersCancelAllRequest
    ): Promise<WebsocketApiResponse<OpenOrdersCancelAllResponse>> {
        return this.websocketBase.sendMessage<OpenOrdersCancelAllResponse>(
            '/openOrders.cancelAll'.slice(1),
            requestParameters as unknown as WebsocketSendMsgOptions,
            { isSigned: true, withApiKey: false }
        );
    }

    /**
     * Reduce the quantity of an existing open order.
     *
     * This adds 0 orders to the `EXCHANGE_MAX_ORDERS` filter and the `MAX_NUM_ORDERS` filter.
     *
     * Read [Order Amend Keep Priority FAQ](/products/spot/faqs/order_amend_keep_priority) to learn more.
     *
     * Weight(IP): 4
     *
     * Unfilled Order Count: 0
     *
     * Security Type: TRADE
     *
     * Notes:
     **Data Source:** Matching Engine
     *
     * @summary Order Amend Keep Priority (TRADE)
     * @param {OrderAmendKeepPriorityRequest} requestParameters Request parameters.
     * @returns {Promise<OrderAmendKeepPriorityResponse>}
     * @memberof TradeApi
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-spot-trading/api/ws-api/trade#order-amend-keep-priority Binance API Documentation}
     */
    public orderAmendKeepPriority(
        requestParameters: OrderAmendKeepPriorityRequest
    ): Promise<WebsocketApiResponse<OrderAmendKeepPriorityResponse>> {
        return this.websocketBase.sendMessage<OrderAmendKeepPriorityResponse>(
            '/order.amend.keepPriority'.slice(1),
            requestParameters as unknown as WebsocketSendMsgOptions,
            { isSigned: true, withApiKey: false }
        );
    }

    /**
     * Cancel an active order.
     *
     * Weight(IP): 1
     *
     * Security Type: TRADE
     *
     * Notes:
     **Data Source:** Matching Engine
     *
     * Notes:
     *
     * If both `orderId` and `origClientOrderId` parameters are provided, the `orderId` is searched first, then the `origClientOrderId` from that result is checked against that order. If both conditions are not met the request will be rejected.
     *
     * `newClientOrderId` will replace `clientOrderId` of the canceled order, freeing it up for new orders.
     *
     * If you cancel an order that is a part of an order list, the entire order list is canceled.
     *
     * The performance for canceling an order (single cancel or as part of a cancel-replace) is always better when only `orderId` is sent. Sending `origClientOrderId` or both `orderId` + `origClientOrderId` will be slower.
     *
     * @summary Cancel order (TRADE)
     * @param {OrderCancelRequest} requestParameters Request parameters.
     * @returns {Promise<OrderCancelResponse>}
     * @memberof TradeApi
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-spot-trading/api/ws-api/trade#order-cancel Binance API Documentation}
     */
    public orderCancel(
        requestParameters: OrderCancelRequest
    ): Promise<WebsocketApiResponse<OrderCancelResponse>> {
        return this.websocketBase.sendMessage<OrderCancelResponse>(
            '/order.cancel'.slice(1),
            requestParameters as unknown as WebsocketSendMsgOptions,
            { isSigned: true, withApiKey: false }
        );
    }

    /**
     * * Cancel an existing order and immediately place a new order instead of the canceled one.
     * A new order that was not attempted (i.e. when `newOrderResult: NOT_ATTEMPTED`), will still increase the unfilled order count by 1.
     * You can only cancel an individual order from an orderList using this method, but the result is the same as canceling the entire orderList.not attempted (i.e. when `newOrderResult: NOT_ATTEMPTED`), will still increase the unfilled order count by 1.
     *
     * Weight(IP): 1
     *
     * Unfilled Order Count: 1
     *
     * Security Type: TRADE
     *
     * Notes:
     **Data Source:** Matching Engine
     *
     * Similar to the [`order.place`](#order-place) request,
     * additional mandatory parameters (*) are determined by the new order `type`.
     *
     * Available `cancelReplaceMode` options:
     *
     * `STOP_ON_FAILURE` – if cancellation request fails, new order placement will not be attempted.
     * `ALLOW_FAILURE` – new order placement will be attempted even if the cancel request fails.
     *
     * <table>
     * <thead>
     * <tr>
     * <th colspan=3 align=left>Request</th>
     * <th colspan=3 align=left>Response</th>
     * </tr>
     * <tr>
     * <th><code>cancelReplaceMode</code></th>
     * <th><code>orderRateLimitExceededMode</code></th>
     * <th>Unfilled Order Count</th>
     * <th><code>cancelResult</code></th>
     * <th><code>newOrderResult</code></th>
     * <th><code>status</code></th>
     * </tr>
     * </thead>
     * <tbody>
     * <tr>
     * <td rowspan="11"><code>STOP_ON_FAILURE</code></td>
     * <td rowspan="6"><code>DO_NOTHING</code></td>
     * <td rowspan="3">Within Limits</td>
     * <td>✅ <code>SUCCESS</code></td>
     * <td>✅ <code>SUCCESS</code></td>
     * <td align=right><code>200</code></td>
     * </tr>
     * <tr>
     * <td>❌ <code>FAILURE</code></td>
     * <td>➖ <code>NOT_ATTEMPTED</code></td>
     * <td align=right><code>400</code></td>
     * </tr>
     * <tr>
     * <td>✅ <code>SUCCESS</code></td>
     * <td>❌ <code>FAILURE</code></td>
     * <td align=right><code>409</code></td>
     * </tr>
     * <tr>
     * <td rowspan="3">Exceeds Limits</td>
     * <td>✅ <code>SUCCESS</code></td>
     * <td>✅ <code>SUCCESS</code></td>
     * <td align=right>N/A</td>
     * </tr>
     * <tr>
     * <td>❌ <code>FAILURE</code></td>
     * <td>➖ <code>NOT_ATTEMPTED</code></td>
     * <td align=right>N/A</td>
     * </tr>
     * <tr>
     * <td>✅ <code>SUCCESS</code></td>
     * <td>❌ <code>FAILURE</code></td>
     * <td align=right>N/A</td>
     * </tr>
     * <tr>
     * <td rowspan="5"><code>CANCEL_ONLY</code></td>
     * <td rowspan="3">Within Limits</td>
     * <td>✅ <code>SUCCESS</code></td>
     * <td>✅ <code>SUCCESS</code></td>
     * <td align=right><code>200</code></td>
     * </tr>
     * <tr>
     * <td>❌ <code>FAILURE</code></td>
     * <td>➖ <code>NOT_ATTEMPTED</code></td>
     * <td align=right><code>400</code></td>
     * </tr>
     * <tr>
     * <td>✅ <code>SUCCESS</code></td>
     * <td>❌ <code>FAILURE</code></td>
     * <td align=right><code>409</code></td>
     * </tr>
     * <tr>
     * <td rowspan="2">Exceeds Limits</td>
     * <td>❌ <code>FAILURE</code></td>
     * <td>➖ <code>NOT_ATTEMPTED</code></td>
     * <td align=right><code>429</code></td>
     * </tr>
     * <tr>
     * <td>✅ <code>SUCCESS</code></td>
     * <td>❌ <code>FAILURE</code></td>
     * <td align=right><code>429</code></td>
     * </tr>
     * <tr>
     * <td rowspan="16"><code>ALLOW_FAILURE</code></td>
     * <td rowspan="8"><code>DO_NOTHING</code></td>
     * <td rowspan="4">Within Limits</td>
     * <td>✅ <code>SUCCESS</code></td>
     * <td>✅ <code>SUCCESS</code></td>
     * <td align=right><code>200</code></td>
     * </tr>
     * <tr>
     * <td>❌ <code>FAILURE</code></td>
     * <td>❌ <code>FAILURE</code></td>
     * <td align=right><code>400</code></td>
     * </tr>
     * <tr>
     * <td>❌ <code>FAILURE</code></td>
     * <td>✅ <code>SUCCESS</code></td>
     * <td align=right><code>409</code></td>
     * </tr>
     * <tr>
     * <td>✅ <code>SUCCESS</code></td>
     * <td>❌ <code>FAILURE</code></td>
     * <td align=right><code>409</code></td>
     * </tr>
     * <tr>
     * <td rowspan="4">Exceeds Limits</td>
     * <td>✅ <code>SUCCESS</code></td>
     * <td>✅ <code>SUCCESS</code></td>
     * <td align=right>N/A</td>
     * </tr>
     * <tr>
     * <td>❌ <code>FAILURE</code></td>
     * <td>❌ <code>FAILURE</code></td>
     * <td align=right>N/A</td>
     * </tr>
     * <tr>
     * <td>❌ <code>FAILURE</code></td>
     * <td>✅ <code>SUCCESS</code></td>
     * <td align=right>N/A</td>
     * </tr>
     * <tr>
     * <td>✅ <code>SUCCESS</code></td>
     * <td>❌ <code>FAILURE</code></td>
     * <td align=right>N/A</td>
     * </tr>
     * <tr>
     * <td rowspan="8"><CODE>CANCEL_ONLY</CODE></td>
     * <td rowspan="4">Within Limits</td>
     * <td>✅ <code>SUCCESS</code></td>
     * <td>✅ <code>SUCCESS</code></td>
     * <td align=right><code>200</code></td>
     * </tr>
     * <tr>
     * <td>❌ <code>FAILURE</code></td>
     * <td>❌ <code>FAILURE</code></td>
     * <td align=right><code>400</code></td>
     * </tr>
     * <tr>
     * <td>❌ <code>FAILURE</code></td>
     * <td>✅ <code>SUCCESS</code></td>
     * <td align=right><code>409</code></td>
     * </tr>
     * <tr>
     * <td>✅ <code>SUCCESS</code></td>
     * <td>❌ <code>FAILURE</code></td>
     * <td align=right><code>409</code></td>
     * </tr>
     * <tr>
     * <td rowspan="4">Exceeds Limits</td>
     * <td>✅ <code>SUCCESS</code></td>
     * <td>✅ <code>SUCCESS</code></td>
     * <td align=right><code>200</code></td>
     * </tr>
     * <tr>
     * <td>❌ <code>FAILURE</code></td>
     * <td>❌ <code>FAILURE</code></td>
     * <td align=right><code>400</code></td>
     * </tr>
     * <tr>
     * <td>❌ <code>FAILURE</code></td>
     * <td>✅ <code>SUCCESS</code></td>
     * <td align=right>N/A</td>
     * </tr>
     * <tr>
     * <td>✅ <code>SUCCESS</code></td>
     * <td>❌ <code>FAILURE</code></td>
     * <td align=right><code>409</code></td>
     * </tr>
     * </tbody>
     * </table>
     *
     * Notes:
     *
     * If both `cancelOrderId` and `cancelOrigClientOrderId` parameters are provided, the `cancelOrderId` is searched first, then the `cancelOrigClientOrderId` from that result is checked against that order. If both conditions are not met the request will be rejected.
     *
     * `cancelNewClientOrderId` will replace `clientOrderId` of the canceled order, freeing it up for new orders.
     *
     * `newClientOrderId` specifies `clientOrderId` value for the placed order.
     *
     * A new order with the same `clientOrderId` is accepted only when the previous one is filled or expired.
     *
     * The new order can reuse old `clientOrderId` of the canceled order.
     *
     * This cancel-replace operation is **not transactional**.
     *
     * If one operation succeeds but the other one fails, the successful operation is still executed.
     *
     * For example, in `STOP_ON_FAILURE` mode, if the new order placement fails, the old order is still canceled.
     *
     * Filters and order count limits are evaluated before cancellation and order placement occurs.
     *
     * If new order placement is not attempted, your order count is still incremented.
     *
     * Like [`order.cancel`](#order-cancel), if you cancel an individual order from an order list, the entire order list is canceled.
     *
     * The performance for canceling an order (single cancel or as part of a cancel-replace) is always better when only `orderId` is sent. Sending `origClientOrderId` or both `orderId` + `origClientOrderId` will be slower.
     *
     * @summary Cancel and replace order (TRADE)
     * @param {OrderCancelReplaceRequest} requestParameters Request parameters.
     * @returns {Promise<OrderCancelReplaceResponse>}
     * @memberof TradeApi
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-spot-trading/api/ws-api/trade#order-cancel-replace Binance API Documentation}
     */
    public orderCancelReplace(
        requestParameters: OrderCancelReplaceRequest
    ): Promise<WebsocketApiResponse<OrderCancelReplaceResponse>> {
        return this.websocketBase.sendMessage<OrderCancelReplaceResponse>(
            '/order.cancelReplace'.slice(1),
            requestParameters as unknown as WebsocketSendMsgOptions,
            { isSigned: true, withApiKey: false }
        );
    }

    /**
     * Cancel an active order list.
     *
     * Weight(IP): 1
     *
     * Security Type: TRADE
     *
     * Notes:
     **Data Source:** Matching Engine
     *
     * Notes:
     *
     * If both `orderListId` and `listClientOrderId` parameters are provided, the `orderListId` is searched first, then the `listClientOrderId` from that result is checked against that order. If both conditions are not met the request will be rejected.
     *
     * Canceling an individual order with [`order.cancel`](#order-cancel) will cancel the entire order list as well.
     *
     * @summary Cancel Order list (TRADE)
     * @param {OrderListCancelRequest} requestParameters Request parameters.
     * @returns {Promise<OrderListCancelResponse>}
     * @memberof TradeApi
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-spot-trading/api/ws-api/trade#order-list-cancel Binance API Documentation}
     */
    public orderListCancel(
        requestParameters: OrderListCancelRequest
    ): Promise<WebsocketApiResponse<OrderListCancelResponse>> {
        return this.websocketBase.sendMessage<OrderListCancelResponse>(
            '/orderList.cancel'.slice(1),
            requestParameters as unknown as WebsocketSendMsgOptions,
            { isSigned: true, withApiKey: false }
        );
    }

    /**
     * Send in a new one-cancels-the-other (OCO) pair:
     * `LIMIT_MAKER` + `STOP_LOSS`/`STOP_LOSS_LIMIT` orders (called *legs*),
     * where activation of one order immediately cancels the other.
     *
     * This adds 1 order to `EXCHANGE_MAX_ORDERS` filter and the `MAX_NUM_ORDERS` filter
     *
     * Weight(IP): 1
     *
     * Unfilled Order Count: 1
     *
     * Security Type: TRADE
     *
     * Notes:
     **Data Source:** Matching Engine
     *
     * Notes:
     *
     * `listClientOrderId` parameter specifies `listClientOrderId` for the OCO pair.
     *
     * A new OCO with the same `listClientOrderId` is accepted only when the previous one is filled or completely expired.
     *
     * `listClientOrderId` is distinct from `clientOrderId` of individual orders.
     *
     * `limitClientOrderId` and `stopClientOrderId` specify `clientOrderId` values for both legs of the OCO.
     *
     * A new order with the same `clientOrderId` is accepted only when the previous one is filled or expired.
     *
     * Price restrictions on the legs:
     *
     * | `side` | Price relation |
     * | ------ | -------------- |
     * | `BUY`  | `price` < market price < `stopPrice` |
     * | `SELL` | `price` > market price > `stopPrice` |
     *
     * Both legs have the same `quantity`.
     *
     * However, you can set different iceberg quantity for individual legs.
     *
     * If `stopIcebergQty` is used, `stopLimitTimeInForce` must be `GTC`.
     *
     * `trailingDelta` applies only to the `STOP_LOSS`/`STOP_LOSS_LIMIT` leg of the OCO.
     *
     * @summary Place new OCO - Deprecated (TRADE)
     * @param {OrderListPlaceRequest} requestParameters Request parameters.
     * @returns {Promise<OrderListPlaceResponse>}
     * @memberof TradeApi
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-spot-trading/api/ws-api/trade#order-list-place Binance API Documentation}
     */
    public orderListPlace(
        requestParameters: OrderListPlaceRequest
    ): Promise<WebsocketApiResponse<OrderListPlaceResponse>> {
        return this.websocketBase.sendMessage<OrderListPlaceResponse>(
            '/orderList.place'.slice(1),
            requestParameters as unknown as WebsocketSendMsgOptions,
            { isSigned: true, withApiKey: false }
        );
    }

    /**
     * Send in an one-cancels-the-other (OCO) pair, where activation of one order immediately cancels the other.
     *
     * An OCO has 2 orders called the **above order** and **below order**.
     *
     * One of the orders must be a `LIMIT_MAKER/TAKE_PROFIT/TAKE_PROFIT_LIMIT` order and the other must be
     * `STOP_LOSS` or `STOP_LOSS_LIMIT` order.
     *
     * Price restrictions:
     * If the OCO is on the `SELL` side:
     * `LIMIT_MAKER/TAKE_PROFIT_LIMIT` `price` > Last Traded Price > `STOP_LOSS/STOP_LOSS_LIMIT`
     * `stopPrice`
     * `TAKE_PROFIT stopPrice` > Last Traded Price > `STOP_LOSS/STOP_LOSS_LIMIT
     * stopPrice`
     * If the OCO is on the `BUY` side:
     * `LIMIT_MAKER` `price` < Last Traded Price < `STOP_LOSS/STOP_LOSS_LIMIT` `stopPrice`
     * `TAKE_PROFIT stopPrice` > Last Traded Price > `STOP_LOSS/STOP_LOSS_LIMIT stopPrice`
     *  OCOs add **2 orders** to the `EXCHANGE_MAX_ORDERS` filter and `MAX_NUM_ORDERS` filter.
     *
     * Weight(IP): 1
     *
     * Unfilled Order Count: 2
     *
     * Security Type: TRADE
     *
     * Notes:
     **Data Source:** Matching Engine
     *
     * @summary Place new Order list - OCO (TRADE)
     * @param {OrderListPlaceOcoRequest} requestParameters Request parameters.
     * @returns {Promise<OrderListPlaceOcoResponse>}
     * @memberof TradeApi
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-spot-trading/api/ws-api/trade#order-list-place-oco Binance API Documentation}
     */
    public orderListPlaceOco(
        requestParameters: OrderListPlaceOcoRequest
    ): Promise<WebsocketApiResponse<OrderListPlaceOcoResponse>> {
        return this.websocketBase.sendMessage<OrderListPlaceOcoResponse>(
            '/orderList.place.oco'.slice(1),
            requestParameters as unknown as WebsocketSendMsgOptions,
            { isSigned: true, withApiKey: false }
        );
    }

    /**
     * Place an [OPO](/products/spot/faqs/opo).
     *
     * OPOs add 2 orders to the EXCHANGE_MAX_NUM_ORDERS filter and MAX_NUM_ORDERS filter.
     *
     * Weight(IP): 1
     *
     * Unfilled Order Count: 2
     *
     * Security Type: TRADE
     *
     * Notes:
     **Data Source:** Matching Engine
     *
     * @summary OPO (TRADE)
     * @param {OrderListPlaceOpoRequest} requestParameters Request parameters.
     * @returns {Promise<OrderListPlaceOpoResponse>}
     * @memberof TradeApi
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-spot-trading/api/ws-api/trade#order-list-place-opo Binance API Documentation}
     */
    public orderListPlaceOpo(
        requestParameters: OrderListPlaceOpoRequest
    ): Promise<WebsocketApiResponse<OrderListPlaceOpoResponse>> {
        return this.websocketBase.sendMessage<OrderListPlaceOpoResponse>(
            '/orderList.place.opo'.slice(1),
            requestParameters as unknown as WebsocketSendMsgOptions,
            { isSigned: true, withApiKey: false }
        );
    }

    /**
     * Place an [OPOCO](/products/spot/faqs/opo).
     *
     * Weight(IP): 1
     *
     * Unfilled Order Count: 3
     *
     * Security Type: TRADE
     *
     * Notes:
     **Data Source:** Matching Engine
     *
     * @summary OPOCO (TRADE)
     * @param {OrderListPlaceOpocoRequest} requestParameters Request parameters.
     * @returns {Promise<OrderListPlaceOpocoResponse>}
     * @memberof TradeApi
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-spot-trading/api/ws-api/trade#order-list-place-opoco Binance API Documentation}
     */
    public orderListPlaceOpoco(
        requestParameters: OrderListPlaceOpocoRequest
    ): Promise<WebsocketApiResponse<OrderListPlaceOpocoResponse>> {
        return this.websocketBase.sendMessage<OrderListPlaceOpocoResponse>(
            '/orderList.place.opoco'.slice(1),
            requestParameters as unknown as WebsocketSendMsgOptions,
            { isSigned: true, withApiKey: false }
        );
    }

    /**
     * Places an OTO.
     *
     * An OTO (One-Triggers-the-Other) is an order list comprised of 2 orders.
     *
     * The first order is called the **working order** and must be `LIMIT` or `LIMIT_MAKER`. Initially, only the
     * working order goes on the order book.
     *
     * The second order is called the **pending order**. It can be any order type except for `MARKET` orders using
     * parameter `quoteOrderQty`. The pending order is only placed on the order book when the working order gets
     **fully filled**.
     *
     * If either the working order or the pending order is cancelled individually, the other order in the order list
     * will also be canceled or expired.
     *
     * When the order list is placed, if the working order gets **immediately fully filled**, the placement response
     * will show the working order as `FILLED` but the pending order will still appear as `PENDING_NEW`. You need to
     * query the status of the pending order again to see its updated status.
     *
     * OTOs add **2 orders** to the `EXCHANGE_MAX_NUM_ORDERS` filter and `MAX_NUM_ORDERS` filter.
     *
     * Weight(IP): 1
     *
     * Unfilled Order Count: 2
     *
     * Security Type: TRADE
     *
     * Notes:
     **Data Source:** Matching Engine
     *
     **Mandatory parameters based on `pendingType` or `workingType`**
     *
     * Depending on the `pendingType` or `workingType`, some optional parameters will become mandatory.
     *
     * |Type                                                  |Additional mandatory parameters|Additional information|
     * |----                                                  |----                           |------
     * |`workingType` = `LIMIT`                               |`workingTimeInForce`           |
     * |`pendingType` = `LIMIT`                                |`pendingPrice`, `pendingTimeInForce`          |
     * |`pendingType` = `STOP_LOSS` or `TAKE_PROFIT`           |`pendingStopPrice` and/or `pendingTrailingDelta`|
     * |`pendingType` =`STOP_LOSS_LIMIT` or `TAKE_PROFIT_LIMIT`|`pendingPrice`, `pendingStopPrice` and/or `pendingTrailingDelta`, `pendingTimeInForce`|
     *
     * @summary Place new Order list - OTO (TRADE)
     * @param {OrderListPlaceOtoRequest} requestParameters Request parameters.
     * @returns {Promise<OrderListPlaceOtoResponse>}
     * @memberof TradeApi
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-spot-trading/api/ws-api/trade#order-list-place-oto Binance API Documentation}
     */
    public orderListPlaceOto(
        requestParameters: OrderListPlaceOtoRequest
    ): Promise<WebsocketApiResponse<OrderListPlaceOtoResponse>> {
        return this.websocketBase.sendMessage<OrderListPlaceOtoResponse>(
            '/orderList.place.oto'.slice(1),
            requestParameters as unknown as WebsocketSendMsgOptions,
            { isSigned: true, withApiKey: false }
        );
    }

    /**
     * Place an OTOCO.
     *
     * An OTOCO (One-Triggers-One-Cancels-the-Other) is an order list comprised of 3 orders.
     * The first order is called the **working order** and must be `LIMIT` or `LIMIT_MAKER`. Initially, only the working order goes on the order book.
     * The behavior of the working order is the same as the [OTO](#order-list-place-oto).
     * OTOCO has 2 pending orders (pending above and pending below), forming an OCO pair. The pending orders are only placed on the order book when the working order gets **fully filled**.
     * The rules of the pending above and pending below follow the same rules as the [Order list OCO](#order-list-place-oco).
     * OTOCOs add **3 orders** to the `EXCHANGE_MAX_NUM_ORDERS` filter and `MAX_NUM_ORDERS` filter.
     *
     * Weight(IP): 1
     *
     * Unfilled Order Count: 3
     *
     * Security Type: TRADE
     *
     * Notes:
     **Data Source:** Matching Engine
     *
     **Mandatory parameters based on `pendingAboveType`, `pendingBelowType` or `workingType`**
     *
     * Depending on the `pendingAboveType`/`pendingBelowType` or `workingType`, some optional parameters will become mandatory.
     *
     * |Type                                                       |Additional mandatory parameters|Additional information|
     * |----                                                       |----                           |------
     * |`workingType` = `LIMIT`                                    |`workingTimeInForce`           |
     * |`pendingAboveType`= `LIMIT_MAKER`                                |`pendingAbovePrice`          |
     * |`pendingAboveType` = `STOP_LOSS/TAKE_PROFIT`         |`pendingAboveStopPrice` and/or `pendingAboveTrailingDelta`|
     * |`pendingAboveType=STOP_LOSS_LIMIT/TAKE_PROFIT_LIMIT`|`pendingAbovePrice`, `pendingAboveStopPrice` and/or `pendingAboveTrailingDelta`, `pendingAboveTimeInForce`|
     * |`pendingBelowType`= `LIMIT_MAKER`                                |`pendingBelowPrice`          |
     * `pendingBelowType= STOP_LOSS/TAKE_PROFIT`         |`pendingBelowStopPrice` and/or `pendingBelowTrailingDelta`|
     * |`pendingBelowType=STOP_LOSS_LIMIT/TAKE_PROFIT_LIMIT`|`pendingBelowPrice`, `pendingBelowStopPrice` and/or `pendingBelowTrailingDelta`, `pendingBelowTimeInForce`|
     *
     * @summary Place new Order list - OTOCO (TRADE)
     * @param {OrderListPlaceOtocoRequest} requestParameters Request parameters.
     * @returns {Promise<OrderListPlaceOtocoResponse>}
     * @memberof TradeApi
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-spot-trading/api/ws-api/trade#order-list-place-otoco Binance API Documentation}
     */
    public orderListPlaceOtoco(
        requestParameters: OrderListPlaceOtocoRequest
    ): Promise<WebsocketApiResponse<OrderListPlaceOtocoResponse>> {
        return this.websocketBase.sendMessage<OrderListPlaceOtocoResponse>(
            '/orderList.place.otoco'.slice(1),
            requestParameters as unknown as WebsocketSendMsgOptions,
            { isSigned: true, withApiKey: false }
        );
    }

    /**
     * Send in a new order.
     *
     * This adds 1 order to the `EXCHANGE_MAX_ORDERS` filter and the `MAX_NUM_ORDERS` filter.
     *
     * Weight(IP): 1
     *
     * Unfilled Order Count: 1
     *
     * Security Type: TRADE
     *
     * Notes:
     **Data Source:** Matching Engine
     *
     * <a id="order-type">Certain parameters (*)</a> become mandatory based on the order `type`:
     *
     * <table>
     * <thead>
     * <tr>
     * <th>Order <code>type</code></th>
     * <th>Mandatory parameters</th>
     * </tr>
     * </thead>
     * <tbody>
     * <tr>
     * <td><code>LIMIT</code></td>
     * <td>
     * <ul>
     * <li><code>timeInForce</code></li>
     * <li><code>price</code></li>
     * <li><code>quantity</code></li>
     * </ul>
     * </td>
     * </tr>
     * <tr>
     * <td><code>LIMIT_MAKER</code></td>
     * <td>
     * <ul>
     * <li><code>price</code></li>
     * <li><code>quantity</code></li>
     * </ul>
     * </td>
     * </tr>
     * <tr>
     * <td><code>MARKET</code></td>
     * <td>
     * <ul>
     * <li><code>quantity</code> or <code>quoteOrderQty</code></li>
     * </ul>
     * </td>
     * </tr>
     * <tr>
     * <td><code>STOP_LOSS</code></td>
     * <td>
     * <ul>
     * <li><code>quantity</code></li>
     * <li><code>stopPrice</code> or <code>trailingDelta</code></li>
     * </ul>
     * </td>
     * </tr>
     * <tr>
     * <td><code>STOP_LOSS_LIMIT</code></td>
     * <td>
     * <ul>
     * <li><code>timeInForce</code></li>
     * <li><code>price</code></li>
     * <li><code>quantity</code></li>
     * <li><code>stopPrice</code> or <code>trailingDelta</code></li>
     * </ul>
     * </td>
     * </tr>
     * <tr>
     * <td><code>TAKE_PROFIT</code></td>
     * <td>
     * <ul>
     * <li><code>quantity</code></li>
     * <li><code>stopPrice</code> or <code>trailingDelta</code></li>
     * </ul>
     * </td>
     * </tr>
     * <tr>
     * <td><code>TAKE_PROFIT_LIMIT</code></td>
     * <td>
     * <ul>
     * <li><code>timeInForce</code></li>
     * <li><code>price</code></li>
     * <li><code>quantity</code></li>
     * <li><code>stopPrice</code> or <code>trailingDelta</code></li>
     * </ul>
     * </td>
     * </tr>
     * </tbody>
     * </table>
     *
     * Supported order types:
     *
     * <table>
     * <thead>
     * <tr>
     * <th>Order <code>type</code></th>
     * <th>Description</th>
     * </tr>
     * </thead>
     * <tbody>
     * <tr>
     * <td><code>LIMIT</code></td>
     * <td>
     * <p>
     * Buy or sell <code>quantity</code> at the specified <code>price</code> or better.
     * </p>
     * </td>
     * </tr>
     * <tr>
     * <td><code>LIMIT_MAKER</code></td>
     * <td>
     * <p>
     * <code>LIMIT</code> order that will be rejected if it immediately matches and trades as a taker.
     * </p>
     * <p>
     * This order type is also known as a POST-ONLY order.
     * </p>
     * </td>
     * </tr>
     * <tr>
     * <td><code>MARKET</code></td>
     * <td>
     * <p>
     * Buy or sell at the best available market price.
     * </p>
     * <ul>
     * <li>
     * <p>
     * <code>MARKET</code> order with <code>quantity</code> parameter
     * specifies the amount of the <em>base asset</em> you want to buy or sell.
     * Actually executed quantity of the quote asset will be determined by available market liquidity.
     * </p>
     * <p>
     * E.g., a MARKET BUY order on BTCUSDT for <code>"quantity": "0.1000"</code>
     * specifies that you want to buy 0.1 BTC at the best available price.
     * If there is not enough BTC at the best price, keep buying at the next best price,
     * until either your order is filled, or you run out of USDT, or market runs out of BTC.
     * </p>
     * </li>
     * <li>
     * <p>
     * <code>MARKET</code> order with <code>quoteOrderQty</code> parameter
     * specifies the amount of the <em>quote asset</em> you want to spend (when buying) or receive (when selling).
     * Actually executed quantity of the base asset will be determined by available market liquidity.
     * </p>
     * <p>
     * E.g., a MARKET BUY on BTCUSDT for <code>"quoteOrderQty": "100.00"</code>
     * specifies that you want to buy as much BTC as you can for 100 USDT at the best available price.
     * Similarly, a SELL order will sell as much available BTC as needed for you to receive 100 USDT
     * (before commission).
     * </p>
     * </li>
     * </ul>
     * </td>
     * </tr>
     * <tr>
     * <td><code>STOP_LOSS</code></td>
     * <td>
     * <p>
     * Execute a <code>MARKET</code> order for given <code>quantity</code> when specified conditions are met.
     * </p>
     * <p>
     * I.e., when <code>stopPrice</code> is reached, or when <code>trailingDelta</code> is activated.
     * </p>
     * </td>
     * </tr>
     * <tr>
     * <td><code>STOP_LOSS_LIMIT</code></td>
     * <td>
     * <p>
     * Place a <code>LIMIT</code> order with given parameters when specified conditions are met.
     * </p>
     * </td>
     * </tr>
     * <tr>
     * <td><code>TAKE_PROFIT</code></td>
     * <td>
     * <p>
     * Like <code>STOP_LOSS</code> but activates when market price moves in the favorable direction.
     * </p>
     * </td>
     * </tr>
     * <tr>
     * <td><code>TAKE_PROFIT_LIMIT</code></td>
     * <td>
     * <p>
     * Like <code>STOP_LOSS_LIMIT</code> but activates when market price moves in the favorable direction.
     * </p>
     * </td>
     * </tr>
     * </tbody>
     * </table>
     *
     * <a id="pegged-orders-info"></a>
     * Notes on using parameters for Pegged Orders:
     *
     * These parameters are allowed for `LIMIT`, `LIMIT_MAKER`, `STOP_LOSS_LIMIT`, `TAKE_PROFIT_LIMIT` orders.
     * If `pegPriceType` is specified, `price` becomes optional. Otherwise, it is still mandatory.
     * `pegPriceType=PRIMARY_PEG` means the primary peg, that is the best price on the same side of the order book as your order.
     * `pegPriceType=MARKET_PEG` means the market peg, that is the best price on the opposite side of the order book from your order.
     * Use `pegOffsetType` and `pegOffsetValue` to request a price level other than the best one. These parameters must be specified together.
     *
     * <a id="timeInForce"></a>
     *
     * Available `timeInForce` options,
     * setting how long the order should be active before expiration:
     *
     * TIF  | Description
     * ----- | --------------
     * `GTC` | **Good 'til Canceled** – the order will remain on the book until you cancel it, or the order is completely filled.
     * `IOC` | **Immediate or Cancel** – the order will be filled for as much as possible, the unfilled quantity immediately expires.
     * `FOK` | **Fill or Kill** – the order will expire unless it cannot be immediately filled for the entire quantity.
     *
     * Notes:
     *
     * `newClientOrderId` specifies `clientOrderId` value for the order.
     *
     * A new order with the same `clientOrderId` is accepted only when the previous one is filled or expired.
     *
     * Any `LIMIT` or `LIMIT_MAKER` order can be made into an iceberg order by specifying the `icebergQty`.
     *
     * An order with an `icebergQty` must have `timeInForce` set to `GTC`.
     *
     * Trigger order price rules for `STOP_LOSS`/`TAKE_PROFIT` orders:
     *
     * `stopPrice` must be above market price: `STOP_LOSS BUY`, `TAKE_PROFIT SELL`
     * `stopPrice` must be below market price: `STOP_LOSS SELL`, `TAKE_PROFIT BUY`
     *
     * `MARKET` orders using `quoteOrderQty` follow [`LOT_SIZE`](/products/spot/filters#lot_size) filter rules.
     *
     * The order will execute a quantity that has notional value as close as possible to requested `quoteOrderQty`.
     *
     * @summary Place new order (TRADE)
     * @param {OrderPlaceRequest} requestParameters Request parameters.
     * @returns {Promise<OrderPlaceResponse>}
     * @memberof TradeApi
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-spot-trading/api/ws-api/trade#order-place Binance API Documentation}
     */
    public orderPlace(
        requestParameters: OrderPlaceRequest
    ): Promise<WebsocketApiResponse<OrderPlaceResponse>> {
        return this.websocketBase.sendMessage<OrderPlaceResponse>(
            '/order.place'.slice(1),
            requestParameters as unknown as WebsocketSendMsgOptions,
            { isSigned: true, withApiKey: false }
        );
    }

    /**
     * Test order placement.
     *
     * Validates new order parameters and verifies your signature
     * but does not send the order into the matching engine.
     *
     * Weight: | Condition | Request Weight |
     * | --- | --- |
     * | Without `computeCommissionRates` | 1 |
     * | With `computeCommissionRates` | 20 |
     *
     * Security Type: TRADE
     *
     * Notes:
     **Data Source:** Memory
     *
     * @summary Test new order (TRADE)
     * @param {OrderTestRequest} requestParameters Request parameters.
     * @returns {Promise<OrderTestResponse>}
     * @memberof TradeApi
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-spot-trading/api/ws-api/trade#order-test Binance API Documentation}
     */
    public orderTest(
        requestParameters: OrderTestRequest
    ): Promise<WebsocketApiResponse<OrderTestResponse>> {
        return this.websocketBase.sendMessage<OrderTestResponse>(
            '/order.test'.slice(1),
            requestParameters as unknown as WebsocketSendMsgOptions,
            { isSigned: true, withApiKey: false }
        );
    }

    /**
     * Places an order using smart order routing (SOR).
     *
     * This adds 1 order to the `EXCHANGE_MAX_ORDERS` filter and the `MAX_NUM_ORDERS` filter.
     *
     * Read [SOR FAQ](/products/spot/faqs/sor_faq) to learn more.
     *
     * Weight(IP): 1
     *
     * Unfilled Order Count: 1
     *
     * Security Type: TRADE
     *
     * Notes:
     **Data Source:** Matching Engine
     *
     **Note:** `sor.order.place` only supports `LIMIT` and `MARKET` orders. `quoteOrderQty` is not supported.
     *
     * @summary Place new order using SOR (TRADE)
     * @param {SorOrderPlaceRequest} requestParameters Request parameters.
     * @returns {Promise<SorOrderPlaceResponse>}
     * @memberof TradeApi
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-spot-trading/api/ws-api/trade#sor-order-place Binance API Documentation}
     */
    public sorOrderPlace(
        requestParameters: SorOrderPlaceRequest
    ): Promise<WebsocketApiResponse<SorOrderPlaceResponse>> {
        return this.websocketBase.sendMessage<SorOrderPlaceResponse>(
            '/sor.order.place'.slice(1),
            requestParameters as unknown as WebsocketSendMsgOptions,
            { isSigned: true, withApiKey: false }
        );
    }

    /**
     * Test new order creation and signature/recvWindow using smart order routing (SOR).
     * Creates and validates a new order but does not send it into the matching engine.
     *
     * Weight: | Condition | Request Weight |
     * | --- | --- |
     * | Without `computeCommissionRates` | 1 |
     * | With `computeCommissionRates` | 20 |
     *
     * Security Type: TRADE
     *
     * Notes:
     **Data Source:** Memory
     *
     * @summary Test new order using SOR (TRADE)
     * @param {SorOrderTestRequest} requestParameters Request parameters.
     * @returns {Promise<SorOrderTestResponse>}
     * @memberof TradeApi
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-spot-trading/api/ws-api/trade#sor-order-test Binance API Documentation}
     */
    public sorOrderTest(
        requestParameters: SorOrderTestRequest
    ): Promise<WebsocketApiResponse<SorOrderTestResponse>> {
        return this.websocketBase.sendMessage<SorOrderTestResponse>(
            '/sor.order.test'.slice(1),
            requestParameters as unknown as WebsocketSendMsgOptions,
            { isSigned: true, withApiKey: false }
        );
    }
}

export enum OrderCancelCancelRestrictionsEnum {
    ONLY_NEW = 'ONLY_NEW',
    ONLY_PARTIALLY_FILLED = 'ONLY_PARTIALLY_FILLED',
}

export enum OrderCancelReplaceCancelReplaceModeEnum {
    STOP_ON_FAILURE = 'STOP_ON_FAILURE',
    ALLOW_FAILURE = 'ALLOW_FAILURE',
}

export enum OrderCancelReplaceSideEnum {
    BUY = 'BUY',
    SELL = 'SELL',
}

export enum OrderCancelReplaceTypeEnum {
    MARKET = 'MARKET',
    LIMIT = 'LIMIT',
    STOP_LOSS = 'STOP_LOSS',
    STOP_LOSS_LIMIT = 'STOP_LOSS_LIMIT',
    TAKE_PROFIT = 'TAKE_PROFIT',
    TAKE_PROFIT_LIMIT = 'TAKE_PROFIT_LIMIT',
    LIMIT_MAKER = 'LIMIT_MAKER',
}

export enum OrderCancelReplaceTimeInForceEnum {
    GTC = 'GTC',
    IOC = 'IOC',
    FOK = 'FOK',
}

export enum OrderCancelReplaceNewOrderRespTypeEnum {
    ACK = 'ACK',
    RESULT = 'RESULT',
    FULL = 'FULL',
}

export enum OrderCancelReplaceSelfTradePreventionModeEnum {
    NONE = 'NONE',
    EXPIRE_TAKER = 'EXPIRE_TAKER',
    EXPIRE_MAKER = 'EXPIRE_MAKER',
    EXPIRE_BOTH = 'EXPIRE_BOTH',
    DECREMENT = 'DECREMENT',
    TRANSFER = 'TRANSFER',
}

export enum OrderCancelReplaceCancelRestrictionsEnum {
    ONLY_NEW = 'ONLY_NEW',
    ONLY_PARTIALLY_FILLED = 'ONLY_PARTIALLY_FILLED',
}

export enum OrderCancelReplaceOrderRateLimitExceededModeEnum {
    DO_NOTHING = 'DO_NOTHING',
    CANCEL_ONLY = 'CANCEL_ONLY',
}

export enum OrderCancelReplacePegPriceTypeEnum {
    PRIMARY_PEG = 'PRIMARY_PEG',
    MARKET_PEG = 'MARKET_PEG',
}

export enum OrderCancelReplacePegOffsetTypeEnum {
    PRICE_LEVEL = 'PRICE_LEVEL',
}

export enum OrderListPlaceSideEnum {
    BUY = 'BUY',
    SELL = 'SELL',
}

export enum OrderListPlaceStopLimitTimeInForceEnum {
    GTC = 'GTC',
    IOC = 'IOC',
    FOK = 'FOK',
}

export enum OrderListPlaceNewOrderRespTypeEnum {
    ACK = 'ACK',
    RESULT = 'RESULT',
    FULL = 'FULL',
}

export enum OrderListPlaceSelfTradePreventionModeEnum {
    NONE = 'NONE',
    EXPIRE_TAKER = 'EXPIRE_TAKER',
    EXPIRE_MAKER = 'EXPIRE_MAKER',
    EXPIRE_BOTH = 'EXPIRE_BOTH',
    DECREMENT = 'DECREMENT',
    TRANSFER = 'TRANSFER',
}

export enum OrderListPlaceOcoSideEnum {
    BUY = 'BUY',
    SELL = 'SELL',
}

export enum OrderListPlaceOcoAboveTypeEnum {
    STOP_LOSS_LIMIT = 'STOP_LOSS_LIMIT',
    STOP_LOSS = 'STOP_LOSS',
    LIMIT_MAKER = 'LIMIT_MAKER',
    TAKE_PROFIT = 'TAKE_PROFIT',
    TAKE_PROFIT_LIMIT = 'TAKE_PROFIT_LIMIT',
}

export enum OrderListPlaceOcoBelowTypeEnum {
    STOP_LOSS = 'STOP_LOSS',
    STOP_LOSS_LIMIT = 'STOP_LOSS_LIMIT',
    TAKE_PROFIT = 'TAKE_PROFIT',
    TAKE_PROFIT_LIMIT = 'TAKE_PROFIT_LIMIT',
}

export enum OrderListPlaceOcoAboveTimeInForceEnum {
    GTC = 'GTC',
    IOC = 'IOC',
    FOK = 'FOK',
}

export enum OrderListPlaceOcoAbovePegPriceTypeEnum {
    PRIMARY_PEG = 'PRIMARY_PEG',
    MARKET_PEG = 'MARKET_PEG',
}

export enum OrderListPlaceOcoAbovePegOffsetTypeEnum {
    PRICE_LEVEL = 'PRICE_LEVEL',
}

export enum OrderListPlaceOcoBelowTimeInForceEnum {
    GTC = 'GTC',
    IOC = 'IOC',
    FOK = 'FOK',
}

export enum OrderListPlaceOcoBelowPegPriceTypeEnum {
    PRIMARY_PEG = 'PRIMARY_PEG',
    MARKET_PEG = 'MARKET_PEG',
}

export enum OrderListPlaceOcoBelowPegOffsetTypeEnum {
    PRICE_LEVEL = 'PRICE_LEVEL',
}

export enum OrderListPlaceOcoNewOrderRespTypeEnum {
    ACK = 'ACK',
    RESULT = 'RESULT',
    FULL = 'FULL',
}

export enum OrderListPlaceOcoSelfTradePreventionModeEnum {
    NONE = 'NONE',
    EXPIRE_TAKER = 'EXPIRE_TAKER',
    EXPIRE_MAKER = 'EXPIRE_MAKER',
    EXPIRE_BOTH = 'EXPIRE_BOTH',
    DECREMENT = 'DECREMENT',
    TRANSFER = 'TRANSFER',
}

export enum OrderListPlaceOpoWorkingTypeEnum {
    LIMIT = 'LIMIT',
    LIMIT_MAKER = 'LIMIT_MAKER',
}

export enum OrderListPlaceOpoWorkingSideEnum {
    BUY = 'BUY',
    SELL = 'SELL',
}

export enum OrderListPlaceOpoPendingTypeEnum {
    LIMIT = 'LIMIT',
    MARKET = 'MARKET',
    STOP_LOSS = 'STOP_LOSS',
    STOP_LOSS_LIMIT = 'STOP_LOSS_LIMIT',
    TAKE_PROFIT = 'TAKE_PROFIT',
    TAKE_PROFIT_LIMIT = 'TAKE_PROFIT_LIMIT',
    LIMIT_MAKER = 'LIMIT_MAKER',
}

export enum OrderListPlaceOpoPendingSideEnum {
    BUY = 'BUY',
    SELL = 'SELL',
}

export enum OrderListPlaceOpoNewOrderRespTypeEnum {
    ACK = 'ACK',
    RESULT = 'RESULT',
    FULL = 'FULL',
}

export enum OrderListPlaceOpoSelfTradePreventionModeEnum {
    NONE = 'NONE',
    EXPIRE_TAKER = 'EXPIRE_TAKER',
    EXPIRE_MAKER = 'EXPIRE_MAKER',
    EXPIRE_BOTH = 'EXPIRE_BOTH',
    DECREMENT = 'DECREMENT',
    TRANSFER = 'TRANSFER',
}

export enum OrderListPlaceOpoWorkingTimeInForceEnum {
    GTC = 'GTC',
    IOC = 'IOC',
    FOK = 'FOK',
}

export enum OrderListPlaceOpoWorkingPegPriceTypeEnum {
    PRIMARY_PEG = 'PRIMARY_PEG',
    MARKET_PEG = 'MARKET_PEG',
}

export enum OrderListPlaceOpoWorkingPegOffsetTypeEnum {
    PRICE_LEVEL = 'PRICE_LEVEL',
}

export enum OrderListPlaceOpoPendingTimeInForceEnum {
    GTC = 'GTC',
    IOC = 'IOC',
    FOK = 'FOK',
}

export enum OrderListPlaceOpoPendingPegPriceTypeEnum {
    PRIMARY_PEG = 'PRIMARY_PEG',
    MARKET_PEG = 'MARKET_PEG',
}

export enum OrderListPlaceOpoPendingPegOffsetTypeEnum {
    PRICE_LEVEL = 'PRICE_LEVEL',
}

export enum OrderListPlaceOpocoWorkingTypeEnum {
    LIMIT = 'LIMIT',
    LIMIT_MAKER = 'LIMIT_MAKER',
}

export enum OrderListPlaceOpocoWorkingSideEnum {
    BUY = 'BUY',
    SELL = 'SELL',
}

export enum OrderListPlaceOpocoPendingSideEnum {
    BUY = 'BUY',
    SELL = 'SELL',
}

export enum OrderListPlaceOpocoPendingAboveTypeEnum {
    STOP_LOSS_LIMIT = 'STOP_LOSS_LIMIT',
    STOP_LOSS = 'STOP_LOSS',
    LIMIT_MAKER = 'LIMIT_MAKER',
    TAKE_PROFIT = 'TAKE_PROFIT',
    TAKE_PROFIT_LIMIT = 'TAKE_PROFIT_LIMIT',
}

export enum OrderListPlaceOpocoNewOrderRespTypeEnum {
    ACK = 'ACK',
    RESULT = 'RESULT',
    FULL = 'FULL',
}

export enum OrderListPlaceOpocoSelfTradePreventionModeEnum {
    NONE = 'NONE',
    EXPIRE_TAKER = 'EXPIRE_TAKER',
    EXPIRE_MAKER = 'EXPIRE_MAKER',
    EXPIRE_BOTH = 'EXPIRE_BOTH',
    DECREMENT = 'DECREMENT',
    TRANSFER = 'TRANSFER',
}

export enum OrderListPlaceOpocoWorkingTimeInForceEnum {
    GTC = 'GTC',
    IOC = 'IOC',
    FOK = 'FOK',
}

export enum OrderListPlaceOpocoWorkingPegPriceTypeEnum {
    PRIMARY_PEG = 'PRIMARY_PEG',
    MARKET_PEG = 'MARKET_PEG',
}

export enum OrderListPlaceOpocoWorkingPegOffsetTypeEnum {
    PRICE_LEVEL = 'PRICE_LEVEL',
}

export enum OrderListPlaceOpocoPendingAboveTimeInForceEnum {
    GTC = 'GTC',
    IOC = 'IOC',
    FOK = 'FOK',
}

export enum OrderListPlaceOpocoPendingAbovePegPriceTypeEnum {
    PRIMARY_PEG = 'PRIMARY_PEG',
    MARKET_PEG = 'MARKET_PEG',
}

export enum OrderListPlaceOpocoPendingAbovePegOffsetTypeEnum {
    PRICE_LEVEL = 'PRICE_LEVEL',
}

export enum OrderListPlaceOpocoPendingBelowTypeEnum {
    STOP_LOSS = 'STOP_LOSS',
    STOP_LOSS_LIMIT = 'STOP_LOSS_LIMIT',
    TAKE_PROFIT = 'TAKE_PROFIT',
    TAKE_PROFIT_LIMIT = 'TAKE_PROFIT_LIMIT',
}

export enum OrderListPlaceOpocoPendingBelowTimeInForceEnum {
    GTC = 'GTC',
    IOC = 'IOC',
    FOK = 'FOK',
}

export enum OrderListPlaceOpocoPendingBelowPegPriceTypeEnum {
    PRIMARY_PEG = 'PRIMARY_PEG',
    MARKET_PEG = 'MARKET_PEG',
}

export enum OrderListPlaceOpocoPendingBelowPegOffsetTypeEnum {
    PRICE_LEVEL = 'PRICE_LEVEL',
}

export enum OrderListPlaceOtoWorkingTypeEnum {
    LIMIT = 'LIMIT',
    LIMIT_MAKER = 'LIMIT_MAKER',
}

export enum OrderListPlaceOtoWorkingSideEnum {
    BUY = 'BUY',
    SELL = 'SELL',
}

export enum OrderListPlaceOtoPendingTypeEnum {
    LIMIT = 'LIMIT',
    MARKET = 'MARKET',
    STOP_LOSS = 'STOP_LOSS',
    STOP_LOSS_LIMIT = 'STOP_LOSS_LIMIT',
    TAKE_PROFIT = 'TAKE_PROFIT',
    TAKE_PROFIT_LIMIT = 'TAKE_PROFIT_LIMIT',
    LIMIT_MAKER = 'LIMIT_MAKER',
}

export enum OrderListPlaceOtoPendingSideEnum {
    BUY = 'BUY',
    SELL = 'SELL',
}

export enum OrderListPlaceOtoNewOrderRespTypeEnum {
    ACK = 'ACK',
    RESULT = 'RESULT',
    FULL = 'FULL',
}

export enum OrderListPlaceOtoSelfTradePreventionModeEnum {
    NONE = 'NONE',
    EXPIRE_TAKER = 'EXPIRE_TAKER',
    EXPIRE_MAKER = 'EXPIRE_MAKER',
    EXPIRE_BOTH = 'EXPIRE_BOTH',
    DECREMENT = 'DECREMENT',
    TRANSFER = 'TRANSFER',
}

export enum OrderListPlaceOtoWorkingTimeInForceEnum {
    GTC = 'GTC',
    IOC = 'IOC',
    FOK = 'FOK',
}

export enum OrderListPlaceOtoWorkingPegPriceTypeEnum {
    PRIMARY_PEG = 'PRIMARY_PEG',
    MARKET_PEG = 'MARKET_PEG',
}

export enum OrderListPlaceOtoWorkingPegOffsetTypeEnum {
    PRICE_LEVEL = 'PRICE_LEVEL',
}

export enum OrderListPlaceOtoPendingTimeInForceEnum {
    GTC = 'GTC',
    IOC = 'IOC',
    FOK = 'FOK',
}

export enum OrderListPlaceOtoPendingPegOffsetTypeEnum {
    PRICE_LEVEL = 'PRICE_LEVEL',
}

export enum OrderListPlaceOtoPendingPegPriceTypeEnum {
    PRIMARY_PEG = 'PRIMARY_PEG',
    MARKET_PEG = 'MARKET_PEG',
}

export enum OrderListPlaceOtocoWorkingTypeEnum {
    LIMIT = 'LIMIT',
    LIMIT_MAKER = 'LIMIT_MAKER',
}

export enum OrderListPlaceOtocoWorkingSideEnum {
    BUY = 'BUY',
    SELL = 'SELL',
}

export enum OrderListPlaceOtocoPendingSideEnum {
    BUY = 'BUY',
    SELL = 'SELL',
}

export enum OrderListPlaceOtocoPendingAboveTypeEnum {
    STOP_LOSS_LIMIT = 'STOP_LOSS_LIMIT',
    STOP_LOSS = 'STOP_LOSS',
    LIMIT_MAKER = 'LIMIT_MAKER',
    TAKE_PROFIT = 'TAKE_PROFIT',
    TAKE_PROFIT_LIMIT = 'TAKE_PROFIT_LIMIT',
}

export enum OrderListPlaceOtocoNewOrderRespTypeEnum {
    ACK = 'ACK',
    RESULT = 'RESULT',
    FULL = 'FULL',
}

export enum OrderListPlaceOtocoSelfTradePreventionModeEnum {
    NONE = 'NONE',
    EXPIRE_TAKER = 'EXPIRE_TAKER',
    EXPIRE_MAKER = 'EXPIRE_MAKER',
    EXPIRE_BOTH = 'EXPIRE_BOTH',
    DECREMENT = 'DECREMENT',
    TRANSFER = 'TRANSFER',
}

export enum OrderListPlaceOtocoWorkingTimeInForceEnum {
    GTC = 'GTC',
    IOC = 'IOC',
    FOK = 'FOK',
}

export enum OrderListPlaceOtocoWorkingPegPriceTypeEnum {
    PRIMARY_PEG = 'PRIMARY_PEG',
    MARKET_PEG = 'MARKET_PEG',
}

export enum OrderListPlaceOtocoWorkingPegOffsetTypeEnum {
    PRICE_LEVEL = 'PRICE_LEVEL',
}

export enum OrderListPlaceOtocoPendingAboveTimeInForceEnum {
    GTC = 'GTC',
    IOC = 'IOC',
    FOK = 'FOK',
}

export enum OrderListPlaceOtocoPendingAbovePegPriceTypeEnum {
    PRIMARY_PEG = 'PRIMARY_PEG',
    MARKET_PEG = 'MARKET_PEG',
}

export enum OrderListPlaceOtocoPendingAbovePegOffsetTypeEnum {
    PRICE_LEVEL = 'PRICE_LEVEL',
}

export enum OrderListPlaceOtocoPendingBelowTypeEnum {
    STOP_LOSS = 'STOP_LOSS',
    STOP_LOSS_LIMIT = 'STOP_LOSS_LIMIT',
    TAKE_PROFIT = 'TAKE_PROFIT',
    TAKE_PROFIT_LIMIT = 'TAKE_PROFIT_LIMIT',
}

export enum OrderListPlaceOtocoPendingBelowTimeInForceEnum {
    GTC = 'GTC',
    IOC = 'IOC',
    FOK = 'FOK',
}

export enum OrderListPlaceOtocoPendingBelowPegPriceTypeEnum {
    PRIMARY_PEG = 'PRIMARY_PEG',
    MARKET_PEG = 'MARKET_PEG',
}

export enum OrderListPlaceOtocoPendingBelowPegOffsetTypeEnum {
    PRICE_LEVEL = 'PRICE_LEVEL',
}

export enum OrderPlaceSideEnum {
    BUY = 'BUY',
    SELL = 'SELL',
}

export enum OrderPlaceTypeEnum {
    MARKET = 'MARKET',
    LIMIT = 'LIMIT',
    STOP_LOSS = 'STOP_LOSS',
    STOP_LOSS_LIMIT = 'STOP_LOSS_LIMIT',
    TAKE_PROFIT = 'TAKE_PROFIT',
    TAKE_PROFIT_LIMIT = 'TAKE_PROFIT_LIMIT',
    LIMIT_MAKER = 'LIMIT_MAKER',
}

export enum OrderPlaceTimeInForceEnum {
    GTC = 'GTC',
    IOC = 'IOC',
    FOK = 'FOK',
}

export enum OrderPlaceNewOrderRespTypeEnum {
    ACK = 'ACK',
    RESULT = 'RESULT',
    FULL = 'FULL',
}

export enum OrderPlaceSelfTradePreventionModeEnum {
    NONE = 'NONE',
    EXPIRE_TAKER = 'EXPIRE_TAKER',
    EXPIRE_MAKER = 'EXPIRE_MAKER',
    EXPIRE_BOTH = 'EXPIRE_BOTH',
    DECREMENT = 'DECREMENT',
    TRANSFER = 'TRANSFER',
}

export enum OrderPlacePegPriceTypeEnum {
    PRIMARY_PEG = 'PRIMARY_PEG',
    MARKET_PEG = 'MARKET_PEG',
}

export enum OrderPlacePegOffsetTypeEnum {
    PRICE_LEVEL = 'PRICE_LEVEL',
}

export enum OrderTestSideEnum {
    BUY = 'BUY',
    SELL = 'SELL',
}

export enum OrderTestTypeEnum {
    MARKET = 'MARKET',
    LIMIT = 'LIMIT',
    STOP_LOSS = 'STOP_LOSS',
    STOP_LOSS_LIMIT = 'STOP_LOSS_LIMIT',
    TAKE_PROFIT = 'TAKE_PROFIT',
    TAKE_PROFIT_LIMIT = 'TAKE_PROFIT_LIMIT',
    LIMIT_MAKER = 'LIMIT_MAKER',
}

export enum OrderTestTimeInForceEnum {
    GTC = 'GTC',
    IOC = 'IOC',
    FOK = 'FOK',
}

export enum OrderTestNewOrderRespTypeEnum {
    ACK = 'ACK',
    RESULT = 'RESULT',
    FULL = 'FULL',
}

export enum OrderTestSelfTradePreventionModeEnum {
    NONE = 'NONE',
    EXPIRE_TAKER = 'EXPIRE_TAKER',
    EXPIRE_MAKER = 'EXPIRE_MAKER',
    EXPIRE_BOTH = 'EXPIRE_BOTH',
    DECREMENT = 'DECREMENT',
    TRANSFER = 'TRANSFER',
}

export enum OrderTestPegPriceTypeEnum {
    PRIMARY_PEG = 'PRIMARY_PEG',
    MARKET_PEG = 'MARKET_PEG',
}

export enum OrderTestPegOffsetTypeEnum {
    PRICE_LEVEL = 'PRICE_LEVEL',
}

export enum SorOrderPlaceSideEnum {
    BUY = 'BUY',
    SELL = 'SELL',
}

export enum SorOrderPlaceTypeEnum {
    MARKET = 'MARKET',
    LIMIT = 'LIMIT',
}

export enum SorOrderPlaceTimeInForceEnum {
    GTC = 'GTC',
    IOC = 'IOC',
    FOK = 'FOK',
}

export enum SorOrderPlaceNewOrderRespTypeEnum {
    ACK = 'ACK',
    RESULT = 'RESULT',
    FULL = 'FULL',
}

export enum SorOrderPlaceSelfTradePreventionModeEnum {
    NONE = 'NONE',
    EXPIRE_TAKER = 'EXPIRE_TAKER',
    EXPIRE_MAKER = 'EXPIRE_MAKER',
    EXPIRE_BOTH = 'EXPIRE_BOTH',
    DECREMENT = 'DECREMENT',
    TRANSFER = 'TRANSFER',
}

export enum SorOrderTestSideEnum {
    BUY = 'BUY',
    SELL = 'SELL',
}

export enum SorOrderTestTypeEnum {
    MARKET = 'MARKET',
    LIMIT = 'LIMIT',
}

export enum SorOrderTestTimeInForceEnum {
    GTC = 'GTC',
    IOC = 'IOC',
    FOK = 'FOK',
}

export enum SorOrderTestNewOrderRespTypeEnum {
    ACK = 'ACK',
    RESULT = 'RESULT',
    FULL = 'FULL',
}

export enum SorOrderTestSelfTradePreventionModeEnum {
    NONE = 'NONE',
    EXPIRE_TAKER = 'EXPIRE_TAKER',
    EXPIRE_MAKER = 'EXPIRE_MAKER',
    EXPIRE_BOTH = 'EXPIRE_BOTH',
    DECREMENT = 'DECREMENT',
    TRANSFER = 'TRANSFER',
}
